import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { calculateLayout, suggestedRowSpacing, polygonCentroid, computeShadowPolygons, computeParapetShadows, panelKey, roofSurface, sunOffsetAt, computeRowShading, latlngToXY, xyToLatlng } from "@/utils/panelCalc";
import { Sun, MapPin, Pencil, Slash, Trash2, Save, FileDown,
         ChevronDown, Search, Layers, X, Info, Cpu, Square, Undo2, Redo2,
         Compass, Copy, Ruler, Box } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { distLatLng, fmtMM, createEdgeLabel, bearingDeg, nearestEdgeIndex, rectangleCentredOn, edgeStats, resizeEdge, isRectangle } from "./solar-design/utils";
import { SpecInput } from "./solar-design/SpecInput";
import { exportSolarPDF } from "./solar-design/exportPDF";
import { useSolarSearch } from "./solar-design/useSolarSearch";
import { useSolarSave } from "./solar-design/useSolarSave";
import Layout3DModal from "./solar-design/Layout3D";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SATELLITE_TILES = "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}";

const DEFAULT_SPEC = {
  panelLengthM: 2.1, panelWidthM: 1.0, panelWattageW: 540,
  azimuthDeg: 180, tiltDeg: 15, interRowSpacingM: 0.8,
  colGapM: 0.02, setbackM: 0.3,
  orientation: "portrait", modulesPerGroup: 1,
  roofHeightM: 0, parapetHeightM: 0, mountType: "flat",
  roofShape: "flat", roofPitchDeg: 0,          // sloped roofs: single slope or two-sided (gable), in degrees
};

const PANEL_COLOR    = "#3B82F6";
const OBSTACLE_COLOR = "#EF4444";
const ROOF_COLOR     = "#F59E0B";

// Distance between two lat/lng points in metres
export default function SolarDesignPage() {
  const { id: projectId } = useParams();   // route is projects/:id/solar-design
  const [searchParams] = useSearchParams();
  const leadIdFromRoute = searchParams.get("lead_id") || null;
  const { user }      = useAuth();

  // Map refs — never touched by React reconciler
  const mapContainerRef    = useRef(null);
  const mapRef             = useRef(null);
  const panelLayerRef      = useRef(null);
  const roofLayerRef       = useRef(null);
  const obstacleLayerRef   = useRef(null);
  const shadowLayerRef     = useRef(null);
  const measureLayerRef    = useRef(null); // permanent roof measurements
  const obsMeasureLayerRef = useRef(null); // obstacle measurements (show on select)
  const tempMeasureRef     = useRef([]); // temp measurement labels during drawing
  const liveMeasureRef     = useRef(null); // dynamic measurement marker following cursor
  const tempPolyRef        = useRef(null);
  const tempMarkersRef     = useRef([]);

  // Stable refs for draw state (avoid stale closures in Leaflet handlers)
  const drawModeRef   = useRef("none");
  const drawPointsRef = useRef([]);
  const specRef       = useRef(DEFAULT_SPEC); // tracks current spec for stale closures
  const dragRoofRef   = useRef(null); // { idx, startLatLng, poly, pts } for shift+drag
  const dragObstacleRef = useRef(null); // { idx, startLatLng, poly, pts } for shift+drag obstacles
  const moveModeRef   = useRef(false); // track moveMode for Leaflet closures
  const vertexLayerRef = useRef(null); // vertex handles for corner dragging
  const dragVertexRef  = useRef(null); // { type: 'roof'|'obstacle', polyIdx, vertexIdx, marker }
  const midpointLayerRef = useRef(null); // "add vertex" midpoint handles
  const rectStartRef   = useRef(null);  // first corner while drawing a rectangle
  const rectPreviewRef = useRef(null);   // live rectangle preview layer
  const alignModeRef   = useRef(false);  // edge-align azimuth mode

  // History (undo/redo) — snapshots pulled from refs to avoid stale closures
  const undoRef        = useRef([]);
  const redoRef        = useRef([]);
  const prevSnapRef    = useRef(null);
  const restoringRef   = useRef(false);
  const roofSpecsRef   = useRef([]);
  const roofPolygonsRef = useRef([]);
  const obstaclePolygonsRef = useRef([]);
  const selectedObsIdxRef = useRef(-1);
  const suppressNextClickRef = useRef(false); // a drag just ended — ignore the synthetic click
  const roofNamesRef   = useRef([]);
  const removedPanelsRef = useRef([]);
  const selectedRoofIdxRef = useRef(0);
  const allPanelsRef   = useRef([]);   // last computed panels (tagged), for re-filtering
  const dragMovedRef   = useRef(false); // did the current drag actually move?
  const cappedNotifiedRef = useRef(false); // avoid repeat 'capped' toasts
  const lastVertexClickRef = useRef({ key: null, t: 0 }); // detect dbl-click on a vertex handle

  // React state (UI only)
  const [roofPolygons,      setRoofPolygons]      = useState([]);
  const [roofSpecs,         setRoofSpecs]         = useState([]); // per-roof specs
  const [selectedRoofIdx,   setSelectedRoofIdx]   = useState(0);  // which roof's spec is shown
  const [obstaclePolygons,  setObstaclePolygons]  = useState([]);
  const [obstacleHeights,   setObstacleHeights]   = useState([]); // height per obstacle (metres)
  const [spec,              setSpec]              = useState(DEFAULT_SPEC);
  const [layout,            setLayout]            = useState(null);
  const [drawMode,          setDrawMode]          = useState("none");
  const [moveMode,          setMoveMode]          = useState(false); // move/select mode
  const [selectedObsIdx,    setSelectedObsIdx]    = useState(-1); // selected obstacle (-1 = none)
  const [drawPointCount,    setDrawPointCount]    = useState(0);
  const [calculating,       setCalculating]       = useState(false);
  const [roofNames,         setRoofNames]         = useState([]);   // reserved (unused)
  const [removedPanels,     setRemovedPanels]     = useState([]);   // panel keys removed by user
  const [alignMode,         setAlignMode]         = useState(false);// edge-align azimuth
  const [ctxMenu,           setCtxMenu]           = useState(null); // { x, y, type, idx }
  const [dimsDialog,        setDimsDialog]        = useState(null); // { type: "roof"|"obstacle", idx } | { type: "new-roof"|"new-obstacle" }
  const [show3D,            setShow3D]            = useState(false);
  const [specificYield,     setSpecificYield]     = useState(1500); // kWh/kWp/yr (India ~1400-1600)
  const [dcAcRatio,         setDcAcRatio]         = useState(1.2);  // DC:AC oversizing
  const [, forceHist]                             = useState(0);    // re-render undo/redo buttons

  // Search (custom hook)
  const {
    searchQuery, setSearchQuery, searchResults, setSearchResults,
    searchLoading, handleSearchInput, goToLocation, submitSearch,
  } = useSolarSearch(mapRef);

  // Save / export
  const [designName,   setDesignName]   = useState("Untitled Design");
  const [clientName,   setClientName]   = useState("");
  const [exporting,    setExporting]    = useState(false);
  const [hasUnsaved,   setHasUnsaved]   = useState(false);
  const [moduleBrand,  setModuleBrand]  = useState("");
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [projects,     setProjects]     = useState([]);
  const [linkedProjectId, setLinkedProjectId] = useState(projectId || "");

  // Save/load hook
  const {
    saving, savedDesigns, showSaved, setShowSaved,
    fetchSavedDesigns, saveDesign, deleteDesign,
  } = useSolarSave({
    roofPolygons, obstaclePolygons, obstacleHeights, roofSpecs, spec,
    designName, clientName, moduleBrand, linkedProjectId,
    layout, currentDesignId, setCurrentDesignId,
    setHasUnsaved, projectIdFromRoute: projectId, leadIdFromRoute, removedPanels,
    searchQuery,
    specificYield, dcAcRatio,
    getMapView: () => {
      const m = mapRef.current;
      if (!m) return null;
      const c = m.getCenter();
      return { center: { lat: c.lat, lng: c.lng }, zoom: m.getZoom() };
    },
  });

  // ── Unsaved changes blocker (beforeunload for tab close / refresh) ─────────
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsaved]);

  // Mark dirty on any drawing or spec change
  const markDirty = () => setHasUnsaved(true);

  // Keep specRef in sync with current spec (for stale closures in Leaflet handlers)
  useEffect(() => { specRef.current = spec; }, [spec]);
  useEffect(() => {
    moveModeRef.current = moveMode;
    if (mapRef.current) {
      mapRef.current.getContainer().style.cursor = moveMode ? "move" : "";
    }
  }, [moveMode]);

  // Keep snapshot-source refs fresh (order matters: before history effect)
  useEffect(() => { roofSpecsRef.current = roofSpecs; }, [roofSpecs]);
  useEffect(() => { roofPolygonsRef.current = roofPolygons; }, [roofPolygons]);
  useEffect(() => { obstaclePolygonsRef.current = obstaclePolygons; }, [obstaclePolygons]);
  useEffect(() => { selectedObsIdxRef.current = selectedObsIdx; }, [selectedObsIdx]);
  useEffect(() => { roofNamesRef.current = roofNames; }, [roofNames]);
  useEffect(() => { removedPanelsRef.current = removedPanels; }, [removedPanels]);
  useEffect(() => { selectedRoofIdxRef.current = selectedRoofIdx; }, [selectedRoofIdx]);
  useEffect(() => { alignModeRef.current = alignMode; }, [alignMode]);

  // Disable panel clicks while drawing/moving so map clicks reach the draw handler
  useEffect(() => {
    const pane = mapRef.current?.getPane("panelPane");
    if (pane) pane.style.pointerEvents = (drawMode !== "none" || moveMode) ? "none" : "";
    if (drawMode !== "none") {
      // Hide handles + edge labels so they don't intercept draw clicks
      vertexLayerRef.current?.clearLayers();
      midpointLayerRef.current?.clearLayers();
      measureLayerRef.current?.clearLayers();
    } else {
      // Restore handles for the selected shape when leaving draw mode
      if (selectedObsIdx >= 0 && obstaclePolygons[selectedObsIdx]) showVertexHandles("obstacle", selectedObsIdx);
      else if (selectedRoofIdx >= 0 && roofPolygons[selectedRoofIdx]) showVertexHandles("roof", selectedRoofIdx);
    }
    // Rebuild roof/obstacle/panel layers so their click-interactivity matches the mode
    redrawRoofLayer(roofPolygons);
    redrawObstacleLayer(obstaclePolygons);
    if (allPanelsRef.current.length) renderPanelLayer(allPanelsRef.current, new Set(removedPanelsRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMode, moveMode]);

  // ── Undo / Redo history ────────────────────────────────────────────────────
  const makeSnapshot = () => ({
    roofPolygons:     roofPolygons.map(p => p.map(v => ({ ...v }))),
    roofSpecs:        roofSpecsRef.current.map(s => ({ ...s })),
    roofNames:        [...roofNamesRef.current],
    obstaclePolygons: obstaclePolygons.map(p => p.map(v => ({ ...v }))),
    obstacleHeights:  [...obstacleHeights],
    removedPanels:    [...removedPanelsRef.current],
    selectedRoofIdx:  selectedRoofIdxRef.current,
  });

  const applySnapshot = (s) => {
    restoringRef.current = true;
    setRoofPolygons(s.roofPolygons.map(p => p.map(v => ({ ...v }))));
    setRoofSpecs(s.roofSpecs.map(x => ({ ...x })));
    setRoofNames([...s.roofNames]);
    setObstaclePolygons(s.obstaclePolygons.map(p => p.map(v => ({ ...v }))));
    setObstacleHeights([...s.obstacleHeights]);
    setRemovedPanels([...s.removedPanels]);
    const idx = Math.min(s.selectedRoofIdx, Math.max(0, s.roofPolygons.length - 1));
    setSelectedRoofIdx(idx);
    if (s.roofSpecs[idx]) setSpec(s.roofSpecs[idx]);
    markDirty();
  };

  // Record history whenever the editable geometry/removals change
  const snapKey = (s) => JSON.stringify([s.roofPolygons, s.roofSpecs, s.obstaclePolygons, s.obstacleHeights, s.removedPanels]);
  useEffect(() => {
    const snap = makeSnapshot();
    if (restoringRef.current) { prevSnapRef.current = snap; restoringRef.current = false; return; }
    // Skip no-op changes (e.g. StrictMode double-invoke, mouseup with no movement)
    if (prevSnapRef.current && snapKey(prevSnapRef.current) === snapKey(snap)) {
      prevSnapRef.current = snap;
      return;
    }
    if (prevSnapRef.current) {
      undoRef.current.push(prevSnapRef.current);
      if (undoRef.current.length > 60) undoRef.current.shift();
      redoRef.current = [];
      forceHist(v => v + 1);
    }
    prevSnapRef.current = snap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofPolygons, obstaclePolygons, obstacleHeights, roofNames, removedPanels]);

  const undo = useCallback(() => {
    if (!undoRef.current.length) return;
    redoRef.current.push(makeSnapshot());
    applySnapshot(undoRef.current.pop());
    forceHist(v => v + 1);
    toast.info("Undo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofPolygons, obstaclePolygons, obstacleHeights, roofNames, removedPanels]);

  const redo = useCallback(() => {
    if (!redoRef.current.length) return;
    undoRef.current.push(makeSnapshot());
    applySnapshot(redoRef.current.pop());
    forceHist(v => v + 1);
    toast.info("Redo");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofPolygons, obstaclePolygons, obstacleHeights, roofNames, removedPanels]);

  // Ctrl+Z / Ctrl+Y (or Ctrl+Shift+Z) keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.key.toLowerCase() === "y") || (e.key.toLowerCase() === "z" && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // ── Lazy-load Leaflet (only on this map screen, not the landing page) ─────
  const [leafletReady, setLeafletReady] = useState(!!window.L);
  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    if (!document.getElementById("leaflet-css")) {
      const css = document.createElement("link");
      css.id = "leaflet-css"; css.rel = "stylesheet";
      css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; css.crossOrigin = "";
      document.head.appendChild(css);
    }
    let s = document.getElementById("leaflet-js");
    if (!s) {
      s = document.createElement("script");
      s.id = "leaflet-js"; s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; s.crossOrigin = "";
      document.body.appendChild(s);
    }
    const done = () => setLeafletReady(true);
    s.addEventListener("load", done);
    return () => s.removeEventListener("load", done);
  }, []);

  // ── Init Leaflet (runs once Leaflet is available) ─────────────────────────
  useEffect(() => {
    if (!leafletReady || !window.L) return;
    if (mapRef.current || !mapContainerRef.current) return;

    const L = window.L;
    const map = L.map(mapContainerRef.current, {
      center: [20.5937, 78.9629], zoom: 5,
      zoomControl: false,
      doubleClickZoom: false,   // we use dbl-click to close polygons
    });

    L.tileLayer(SATELLITE_TILES, { attribution: "Imagery © Google", maxZoom: 21, crossOrigin: "anonymous" }).addTo(map);
    L.control.zoom({ position: "topright" }).addTo(map);

    // Dedicated pane for panels so we can disable their clicks while drawing
    map.createPane("panelPane");
    map.getPane("panelPane").style.zIndex = 450;
    // Obstacles sit ABOVE roofs (default overlayPane = 400) so an obstacle drawn
    // inside a roof can always be clicked / dragged — a roof redraw no longer covers it.
    map.createPane("obstaclePane");
    map.getPane("obstaclePane").style.zIndex = 430;

    panelLayerRef.current    = L.layerGroup().addTo(map);
    shadowLayerRef.current   = L.layerGroup().addTo(map);
    obstacleLayerRef.current = L.layerGroup().addTo(map);
    roofLayerRef.current     = L.layerGroup().addTo(map);
    measureLayerRef.current  = L.layerGroup().addTo(map);
    obsMeasureLayerRef.current = L.layerGroup().addTo(map);
    vertexLayerRef.current   = L.layerGroup().addTo(map);
    midpointLayerRef.current = L.layerGroup().addTo(map);

    // IMPORTANT: handlers read from refs, not React state
    map.on("click",    onMapClick);
    map.on("dblclick", onMapDblClick);
    // Clicking empty map deselects the obstacle (shapes stop propagation, so this is "empty map" only)
    map.on("click", () => { if (drawModeRef.current === "none") setSelectedObsIdx(-1); });

    // Roof/obstacle drag + live measurement on mousemove
    map.on("mousemove", (e) => {
      // Rectangle preview while dragging out the second corner
      if (drawModeRef.current === "rect" && rectStartRef.current) {
        const a = rectStartRef.current, b = e.latlng;
        const corners = [[a.lat, a.lng], [a.lat, b.lng], [b.lat, b.lng], [b.lat, a.lng]];
        if (rectPreviewRef.current) rectPreviewRef.current.setLatLngs(corners);
        else rectPreviewRef.current = L.polygon(corners, { color: ROOF_COLOR, fillColor: ROOF_COLOR, fillOpacity: 0.12, weight: 2, dashArray: "6 4", interactive: false }).addTo(map);
      }
      // Live measurement: show distance from last drawn point to cursor
      if (drawModeRef.current !== "none" && drawPointsRef.current.length > 0) {
        const lastPt = drawPointsRef.current[drawPointsRef.current.length - 1];
        const curPt = { lat: e.latlng.lat, lng: e.latlng.lng };
        const d = distLatLng(lastPt, curPt);
        const label = fmtMM(d);
        const midLat = (lastPt.lat + curPt.lat) / 2;
        const midLng = (lastPt.lng + curPt.lng) / 2;

        if (liveMeasureRef.current) map.removeLayer(liveMeasureRef.current);
        const color = drawModeRef.current === "roof" ? "#2F7D52" : "#991B1B";
        liveMeasureRef.current = L.marker([midLat, midLng], {
          icon: L.divIcon({
            className: "",
            html: `<div style="background:${color};color:#fff;font-size:11px;font-weight:700;padding:2px 6px;border-radius:4px;white-space:nowrap;font-family:Manrope,sans-serif;pointer-events:none;transform:translate(-50%,-50%);box-shadow:0 1px 4px rgba(0,0,0,0.3)">${label}</div>`,
            iconSize: [0, 0],
          }),
          interactive: false,
        }).addTo(map);
      } else if (liveMeasureRef.current) {
        map.removeLayer(liveMeasureRef.current);
        liveMeasureRef.current = null;
      }

      // Handle roof drag
      const roofDrag = dragRoofRef.current;
      if (roofDrag) {
        dragMovedRef.current = true;
        const dlat = e.latlng.lat - roofDrag.startLatLng.lat;
        const dlng = e.latlng.lng - roofDrag.startLatLng.lng;
        roofDrag.startLatLng = e.latlng;
        const newPts = roofDrag.pts.map(p => ({ lat: p.lat + dlat, lng: p.lng + dlng }));
        roofDrag.poly.setLatLngs(newPts.map(p => [p.lat, p.lng]));
        roofDrag.pts.forEach((p, j) => { roofDrag.pts[j] = newPts[j]; });
      }
      // Handle obstacle drag
      const obsDrag = dragObstacleRef.current;
      if (obsDrag) {
        dragMovedRef.current = true;
        const dlat = e.latlng.lat - obsDrag.startLatLng.lat;
        const dlng = e.latlng.lng - obsDrag.startLatLng.lng;
        obsDrag.startLatLng = e.latlng;
        const newPts = obsDrag.pts.map(p => ({ lat: p.lat + dlat, lng: p.lng + dlng }));
        obsDrag.poly.setLatLngs(newPts.map(p => [p.lat, p.lng]));
        obsDrag.pts.forEach((p, j) => { obsDrag.pts[j] = newPts[j]; });
      }

      // Handle vertex corner drag
      const vtx = dragVertexRef.current;
      if (vtx) {
        dragMovedRef.current = true;
        vtx.marker.setLatLng(e.latlng);
        // vtx.pts is a direct reference to the polygon's points array
        const pts = vtx.pts;
        if (pts && pts[vtx.vertexIdx]) {
          pts[vtx.vertexIdx] = { lat: e.latlng.lat, lng: e.latlng.lng };
          // Update polygon shape visually
          const layerRef = vtx.type === "roof" ? roofLayerRef : obstacleLayerRef;
          if (layerRef.current) {
            const layers = layerRef.current.getLayers();
            if (layers[vtx.polyIdx]) layers[vtx.polyIdx].setLatLngs(pts.map(p => [p.lat, p.lng]));
          }
          // Update measurements live
          updateVertexMeasurements(vtx.type, vtx.polyIdx, pts);
        }
      }
    });
    map.on("mouseup", () => {
      const moved = dragMovedRef.current;
      if (dragRoofRef.current) {
        dragRoofRef.current = null;
        map.dragging.enable();
        if (moved) { setRoofPolygons(prev => [...prev]); markDirty(); }
      }
      if (dragObstacleRef.current) {
        dragObstacleRef.current = null;
        map.dragging.enable();
        if (moved) { suppressNextClickRef.current = true; setObstaclePolygons(prev => [...prev]); markDirty(); }
      }
      if (dragVertexRef.current) {
        const vtx = dragVertexRef.current;
        dragVertexRef.current = null;
        map.dragging.enable();
        if (moved) {
          if (vtx.type === "roof") setRoofPolygons(prev => [...prev]);
          else setObstaclePolygons(prev => [...prev]);
          markDirty();
        }
      }
      dragMovedRef.current = false;
    });

    mapRef.current = map;
    fetchSavedDesigns();

    return () => { map.off(); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafletReady]);

  // ── ESC key handler — cancel current polygon drawing ──────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && drawModeRef.current !== "none") {
        clearTempDraw();
        drawModeRef.current = "none";
        setDrawMode("none");
        if (mapRef.current) mapRef.current.getContainer().style.cursor = "";
        toast.info("Drawing cancelled");
        return;
      }
      if (e.key === "Escape" && selectedObsIdxRef.current >= 0) { setSelectedObsIdx(-1); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedObsIdxRef.current >= 0) {
        const tag = (e.target && e.target.tagName) || "";
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) return;
        e.preventDefault();
        const i = selectedObsIdxRef.current;
        setObstaclePolygons(prev => prev.filter((_, idx) => idx !== i));
        setObstacleHeights(prev => prev.filter((_, idx) => idx !== i));
        setSelectedObsIdx(-1);
        markDirty();
        toast.info(`Obstacle ${i + 1} deleted`);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ── Stable Leaflet event handlers (read from refs, never recreated) ───────
  const onMapClick = (e) => {
    const mode = drawModeRef.current;
    if (mode === "none") return;
    const L   = window.L;
    const map = mapRef.current;
    const pt  = { lat: e.latlng.lat, lng: e.latlng.lng };

    // Rectangle tool: click corner A, then click opposite corner B
    if (mode === "rect") {
      if (!rectStartRef.current) {
        rectStartRef.current = pt;
        markDirty();
        return;
      }
      const a = rectStartRef.current, b = pt;
      const corners = [
        { lat: a.lat, lng: a.lng },
        { lat: a.lat, lng: b.lng },
        { lat: b.lat, lng: b.lng },
        { lat: b.lat, lng: a.lng },
      ];
      if (rectPreviewRef.current) { map.removeLayer(rectPreviewRef.current); rectPreviewRef.current = null; }
      rectStartRef.current = null;
      addRoofPolygon(corners);
      drawModeRef.current = "none";
      setDrawMode("none");
      map.getContainer().style.cursor = "";
      toast.success("Rectangle roof added");
      return;
    }

    drawPointsRef.current = [...drawPointsRef.current, pt];
    setDrawPointCount(drawPointsRef.current.length);
    markDirty();

    // Redraw live preview line
    if (tempPolyRef.current) map.removeLayer(tempPolyRef.current);
    const color = drawModeRef.current === "roof" ? ROOF_COLOR : OBSTACLE_COLOR;
    tempPolyRef.current = L.polyline(
      drawPointsRef.current.map(p => [p.lat, p.lng]),
      { color, weight: 2, dashArray: "6 4" }
    ).addTo(map);

    // Vertex dot
    const m = L.circleMarker([pt.lat, pt.lng], {
      radius: 5, color, fillColor: "#fff", fillOpacity: 1, weight: 2,
    }).addTo(map);
    tempMarkersRef.current.push(m);

    // Measurement label between last two points
    if (drawPointsRef.current.length >= 2) {
      const prev = drawPointsRef.current[drawPointsRef.current.length - 2];
      const lbl = createEdgeLabel(L, prev, pt, color);
      lbl.addTo(map);
      tempMeasureRef.current.push(lbl);
    }
  };

  const onMapDblClick = (e) => {
    L_stopPropagation(e);
    finishPolygon();
  };

  const L_stopPropagation = (e) => {
    try { window.L.DomEvent.stopPropagation(e); } catch {}
  };

  // ── Add a completed roof polygon (shared by draw + rectangle tools) ─────────
  const addRoofPolygon = useCallback((pts) => {
    setRoofPolygons(prev => { setSelectedRoofIdx(prev.length); return [...prev, pts]; });
    setRoofSpecs(prev => [...prev, { ...specRef.current }]);
    markDirty();
  }, []);

  // ── Finish polygon ─────────────────────────────────────────────────────────
  const finishPolygon = useCallback(() => {
    const L   = window.L;
    const map = mapRef.current;
    const pts = drawPointsRef.current;
    const mode = drawModeRef.current;

    if (!map || !L) return;
    if (mode === "rect") return; // rectangle tool completes on second click
    // Double-click fires extra 'click' events at the same point — strip ALL trailing duplicates
    let cleanPts = pts.slice();
    const near = (a, b) => Math.abs(a.lat - b.lat) < 5e-6 && Math.abs(a.lng - b.lng) < 5e-6;
    while (cleanPts.length > 3 && near(cleanPts[cleanPts.length - 1], cleanPts[cleanPts.length - 2])) {
      cleanPts.pop();
    }
    if (cleanPts.length < 3) { toast.error("Draw at least 3 points to close a polygon"); return; }

    // Clear temp drawing
    if (tempPolyRef.current) { map.removeLayer(tempPolyRef.current); tempPolyRef.current = null; }
    tempMarkersRef.current.forEach(m => map.removeLayer(m));
    tempMarkersRef.current = [];

    if (mode === "roof") {
      addRoofPolygon(cleanPts);
    } else {
      setObstaclePolygons(prev => [...prev, cleanPts]);
      setObstacleHeights(prev => [...prev, mode === "keepout" ? 0 : 1.0]); // keep-out = height 0 (no shadow)
    }

    // Reset draw state and exit draw mode (prevents accidental extra shapes)
    drawPointsRef.current = [];
    setDrawPointCount(0);
    drawModeRef.current = "none";
    setDrawMode("none");
    if (map) map.getContainer().style.cursor = "";
    if (mode !== "roof") setSelectedObsIdx(cleanPts ? obstaclePolygonsRef.current.length : -1); // select the new one
    toast.success(mode === "roof" ? "Roof area added — panels will auto-calculate"
      : "Obstacle marked — set its height in the sidebar, or tick “No shadow” for a walkway / keep-out. Drag it to move, Delete key removes.",
      { duration: 5000 });
  }, [addRoofPolygon]);

  // ── Draw mode control ─────────────────────────────────────────────────────
  const startDraw = (mode) => {
    // Guard: block drawing roofs when zoomed too far out (prevents km-scale roofs / crashes)
    if ((mode === "roof" || mode === "rect") && mapRef.current) {
      const z = mapRef.current.getZoom();
      if (z < 16) {
        toast.warning("Zoom in to your rooftop first (use search or the + button) before drawing.");
        return;
      }
    }
    // Cancel any in-progress drawing first
    clearTempDraw();
    setAlignMode(false);
    setMoveMode(false);
    drawModeRef.current = mode;
    setDrawMode(mode);
    // Set cursor directly on Leaflet container — DO NOT set via React className
    if (mapRef.current) mapRef.current.getContainer().style.cursor = "crosshair";
    toast.info(
      mode === "roof"
        ? "Click on the map to trace the roof outline. Double-click to close."
        : mode === "rect"
        ? "Click one corner of the roof, then click the opposite corner."
        : "Click to trace the obstacle or keep-out area (AC unit, tank, walkway, skylight). Double-click to close.",
      { duration: 4000 }
    );
  };

  const stopDraw = () => {
    clearTempDraw();
    drawModeRef.current = "none";
    setDrawMode("none");
    if (mapRef.current) mapRef.current.getContainer().style.cursor = "";
  };

  const clearTempDraw = () => {
    const map = mapRef.current;
    if (!map) return;
    if (tempPolyRef.current) { map.removeLayer(tempPolyRef.current); tempPolyRef.current = null; }
    tempMarkersRef.current.forEach(m => map.removeLayer(m));
    tempMarkersRef.current = [];
    tempMeasureRef.current.forEach(m => map.removeLayer(m));
    tempMeasureRef.current = [];
    if (liveMeasureRef.current) { map.removeLayer(liveMeasureRef.current); liveMeasureRef.current = null; }
    if (rectPreviewRef.current) { map.removeLayer(rectPreviewRef.current); rectPreviewRef.current = null; }
    rectStartRef.current = null;
    drawPointsRef.current = [];
    setDrawPointCount(0);
  };


  // ── Vertex + edit helpers (insert / delete points) ──────────────────────────
  const deleteVertex = useCallback((type, polyIdx, vIdx) => {
    const setter = type === "roof" ? setRoofPolygons : setObstaclePolygons;
    setter(prev => {
      const poly = prev[polyIdx];
      if (!poly || poly.length <= 3) { toast.error("A shape needs at least 3 points"); return prev; }
      return prev.map((p, i) => (i === polyIdx ? p.filter((_, j) => j !== vIdx) : p));
    });
    markDirty();
    toast.info("Point removed");
  }, []);

  const insertVertex = useCallback((type, polyIdx, edgeIdx) => {
    const setter = type === "roof" ? setRoofPolygons : setObstaclePolygons;
    setter(prev => {
      const poly = prev[polyIdx];
      if (!poly) return prev;
      const a = poly[edgeIdx], b = poly[(edgeIdx + 1) % poly.length];
      const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
      const np = [...poly];
      np.splice(edgeIdx + 1, 0, mid);
      return prev.map((p, i) => (i === polyIdx ? np : p));
    });
    markDirty();
  }, []);

  // ── Vertex handles for corner stretching ────────────────────────────────────
  const showVertexHandles = (type, polyIdx) => {
    const L = window.L;
    if (!L || !vertexLayerRef.current) return;
    vertexLayerRef.current.clearLayers();
    midpointLayerRef.current?.clearLayers();
    // Don't show handles while drawing — they'd intercept the draw clicks
    if (drawModeRef.current !== "none") return;
    const pts = type === "roof" ? roofPolygons[polyIdx] : obstaclePolygons[polyIdx];
    if (!pts) return;
    const color = type === "roof" ? "#2F7D52" : "#991B1B";

    // Corner handles — drag to resize, double-click to delete
    pts.forEach((pt, vIdx) => {
      const handle = L.marker([pt.lat, pt.lng], {
        icon: L.divIcon({
          className: "",
          html: `<div title="Drag to resize · double-click to delete" style="width:14px;height:14px;background:${color};border:2px solid #fff;border-radius:2px;cursor:grab;box-shadow:0 1px 4px rgba(0,0,0,0.5);transform:translate(-8px,-8px)"></div>`,
          iconSize: [0, 0],
        }),
        draggable: false,
        interactive: true,
      });
      handle.on("mousedown", (e) => {
        L.DomEvent.stopPropagation(e);
        if (e.originalEvent && e.originalEvent.button !== 0) return; // left button only
        const now = Date.now();
        const key = `${type}:${polyIdx}:${vIdx}`;
        // Second press on the same handle within 350ms → delete the vertex
        if (lastVertexClickRef.current.key === key && now - lastVertexClickRef.current.t < 350) {
          lastVertexClickRef.current = { key: null, t: 0 };
          dragVertexRef.current = null;
          if (mapRef.current) mapRef.current.dragging.enable();
          deleteVertex(type, polyIdx, vIdx);
          return;
        }
        lastVertexClickRef.current = { key, t: now };
        dragVertexRef.current = { type, polyIdx, vertexIdx: vIdx, marker: handle, pts };
        const map = mapRef.current;
        if (map) map.dragging.disable();
      });
      handle.addTo(vertexLayerRef.current);
    });

    // Midpoint "add point" handles on each edge
    if (midpointLayerRef.current) {
      pts.forEach((pt, i) => {
        const b = pts[(i + 1) % pts.length];
        const mid = { lat: (pt.lat + b.lat) / 2, lng: (pt.lng + b.lng) / 2 };
        const add = L.marker([mid.lat, mid.lng], {
          icon: L.divIcon({
            className: "",
            html: `<div title="Click to add a point" style="width:16px;height:16px;background:#fff;border:2px dashed ${color};border-radius:50%;color:${color};font-size:12px;font-weight:800;line-height:12px;text-align:center;cursor:copy;box-shadow:0 1px 3px rgba(0,0,0,0.4);transform:translate(-9px,-9px)">+</div>`,
            iconSize: [0, 0],
          }),
          interactive: true,
        });
        add.on("mousedown", (e) => {
          L.DomEvent.stopPropagation(e);
          if (e.originalEvent && e.originalEvent.button !== 0) return; // ignore right/middle click
          insertVertex(type, polyIdx, i);
        });
        add.addTo(midpointLayerRef.current);
      });
    }
  };

  const updateVertexMeasurements = (type, polyIdx, pts) => {
    const L = window.L;
    if (!L) return;
    // Update the appropriate measurement layer
    const layer = type === "roof" ? measureLayerRef.current : obsMeasureLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!pts) return;
    const color = type === "roof" ? "#2F7D52" : "#991B1B";
    for (let j = 0; j < pts.length; j++) {
      const a = pts[j];
      const b = pts[(j + 1) % pts.length];
      createEdgeLabel(L, a, b, color).addTo(layer);
    }
    // Also update vertex handle positions
    if (vertexLayerRef.current) {
      const handles = vertexLayerRef.current.getLayers();
      pts.forEach((pt, j) => {
        if (handles[j]) handles[j].setLatLng([pt.lat, pt.lng]);
      });
    }
    // And reposition midpoint "add" handles
    if (midpointLayerRef.current) {
      const mids = midpointLayerRef.current.getLayers();
      pts.forEach((pt, j) => {
        const b = pts[(j + 1) % pts.length];
        if (mids[j]) mids[j].setLatLng([(pt.lat + b.lat) / 2, (pt.lng + b.lng) / 2]);
      });
    }
  };


  const clearAll = () => {
    stopDraw();
    setAlignMode(false);
    setMoveMode(false);
    roofLayerRef.current?.clearLayers();
    obstacleLayerRef.current?.clearLayers();
    panelLayerRef.current?.clearLayers();
    measureLayerRef.current?.clearLayers();
    obsMeasureLayerRef.current?.clearLayers();
    vertexLayerRef.current?.clearLayers();
    midpointLayerRef.current?.clearLayers();
    shadowLayerRef.current?.clearLayers();
    setRoofPolygons([]);
    setRoofSpecs([]);
    setSelectedRoofIdx(0);
    setObstaclePolygons([]);
    setObstacleHeights([]);
    setRemovedPanels([]);
    allPanelsRef.current = [];
    setLayout(null);
    setHasUnsaved(false);
    setCurrentDesignId(null);
  };

  const redrawRoofLayer = (polys) => {
    const L = window.L;
    if (!L || !roofLayerRef.current) return;
    roofLayerRef.current.clearLayers();
    // Clear and rebuild measurement labels
    if (measureLayerRef.current) measureLayerRef.current.clearLayers();
    polys.forEach((pts, i) => {
      const isSelected = i === selectedRoofIdx;
      const poly = L.polygon(pts.map(p => [p.lat, p.lng]), {
        color: isSelected ? "#2F7D52" : ROOF_COLOR,
        fillColor: ROOF_COLOR,
        fillOpacity: isSelected ? 0.2 : 0.08,
        weight: isSelected ? 3 : 2,
        dashArray: isSelected ? null : "8 3",
        interactive: drawModeRef.current === "none", // let draw clicks pass through to the map
      });
      // Permanent edge measurement labels for roofs
      for (let j = 0; j < pts.length; j++) {
        const a = pts[j];
        const b = pts[(j + 1) % pts.length];
        const lbl = createEdgeLabel(L, a, b, isSelected ? "#2F7D52" : "rgba(180,120,0,0.85)");
        if (measureLayerRef.current) lbl.addTo(measureLayerRef.current);
      }
      poly.bindTooltip(`Roof ${i + 1}${isSelected ? " (editing)" : ""} — click: select · right-click: menu`, { permanent: false, sticky: true });
      poly.on("click", (e) => {
        L.DomEvent.stopPropagation(e);
        if (drawModeRef.current !== "none") return;
        // Edge-align azimuth mode
        if (alignModeRef.current) {
          alignRoofToEdgeAt(i, e.latlng);
          return;
        }
        setSelectedRoofIdx(i);
        setSpec(roofSpecs[i] || DEFAULT_SPEC);
        redrawRoofLayer(polys);
        toast.info(`Selected Roof ${i + 1}`);
      });
      poly.on("contextmenu", (e) => {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);
        if (drawModeRef.current !== "none") return;
        setSelectedRoofIdx(i);
        setSpec(roofSpecs[i] || DEFAULT_SPEC);
        setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, type: "roof", idx: i });
      });
      // Drag to move (works in move mode or with Shift key)
      poly.on("mousedown", (e) => {
        if (drawModeRef.current !== "none") return;
        if (moveModeRef.current || e.originalEvent.shiftKey) {
          L.DomEvent.stopPropagation(e);
          dragRoofRef.current = { idx: i, startLatLng: e.latlng, poly, pts };
          const m = mapRef.current;
          if (m) m.dragging.disable();
        }
      });
      poly.addTo(roofLayerRef.current);
      // Sloped roofs: ridge line (gable) and a downhill arrow so the slope direction is visible
      const rs = roofSpecsRef.current[i] || specRef.current;
      if ((rs.roofPitchDeg || 0) > 0 && rs.roofShape && rs.roofShape !== "flat" && rs.mountType === "pitched") {
        const o = polygonCentroid(pts);
        const xy = pts.map(q => latlngToXY(q.lat, q.lng, o.lat, o.lng));
        const sf = roofSurface(xy, { roofShape: rs.roofShape, roofPitchDeg: rs.roofPitchDeg, azimuthDeg: rs.azimuthDeg });
        const back = (q) => xyToLatlng(q.x, q.y, o.lat, o.lng);
        if (sf.ridge && sf.halves.length === 2) {
          const shared = sf.halves[0].poly.filter(q => sf.halves[1].poly.some(r => Math.hypot(r.x - q.x, r.y - q.y) < 1e-6));
          if (shared.length >= 2) {
            const rl = L.polyline([shared[0], shared[shared.length - 1]].map(q => { const ll = back(q); return [ll.lat, ll.lng]; }), { color: "#7C5A1E", weight: 3, dashArray: "2 6", interactive: false });
            rl.bindTooltip(`Ridge · ${rs.roofPitchDeg}° both sides`, { permanent: false, sticky: true });
            rl.addTo(roofLayerRef.current);
          }
        }
        // downhill arrows (one per face)
        sf.halves.forEach(h => {
          const c = h.poly.reduce((acc, q) => ({ x: acc.x + q.x / h.poly.length, y: acc.y + q.y / h.poly.length }), { x: 0, y: 0 });
          const az = h.azimuthDeg * DEG_RAD;
          const len = Math.max(1.5, Math.sqrt(Math.abs(polygonAreaXYLocal(h.poly))) * 0.18);
          const tip = { x: c.x + Math.sin(az) * len, y: c.y + Math.cos(az) * len };
          const a1 = back(c), a2 = back(tip);
          L.polyline([[a1.lat, a1.lng], [a2.lat, a2.lng]], { color: "#7C5A1E", weight: 2, interactive: false }).addTo(roofLayerRef.current);
          L.circleMarker([a2.lat, a2.lng], { radius: 3, color: "#7C5A1E", fillColor: "#7C5A1E", fillOpacity: 1, interactive: false }).bindTooltip(`Slopes down toward ${h.azimuthDeg}°`, { sticky: true }).addTo(roofLayerRef.current);
        });
      }
    });
  };
  const DEG_RAD = Math.PI / 180;
  const polygonAreaXYLocal = (poly) => { let a = 0; for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) a += poly[j].x * poly[i].y - poly[i].x * poly[j].y; return a / 2; };

  const redrawObstacleLayer = (polys) => {
    const L = window.L;
    if (!L || !obstacleLayerRef.current) return;
    obstacleLayerRef.current.clearLayers();
    polys.forEach((pts, i) => {
      const h = obstacleHeights[i] != null ? obstacleHeights[i] : 1.0;
      const isKeepout = h <= 0;
      const baseColor = isKeepout ? "#6B7280" : OBSTACLE_COLOR;
      const isSelected = i === selectedObsIdx;
      const poly = L.polygon(pts.map(p => [p.lat, p.lng]), {
        pane: "obstaclePane",
        color: isSelected ? (isKeepout ? "#374151" : "#991B1B") : baseColor,
        fillColor: baseColor,
        fillOpacity: isSelected ? 0.45 : (isKeepout ? 0.22 : 0.3),
        weight: isSelected ? 3 : 2,
        dashArray: isKeepout ? "5 4" : null,
        interactive: drawModeRef.current === "none", // let draw clicks pass through to the map
      });
      poly.bindTooltip(
        `${isKeepout ? "Keep-out" : "Obstacle"} ${i + 1}${isKeepout ? " (no shadow)" : ` (${h}m)`} — ` +
        (isSelected ? "drag to move · drag corners to reshape · Delete key removes" : "click to select · right-click: menu"),
        { permanent: false, sticky: true });
      // Selected obstacle shows a move cursor
      poly.on("mouseover", () => { if (isSelected && drawModeRef.current === "none" && mapRef.current) mapRef.current.getContainer().style.cursor = "move"; });
      poly.on("mouseout",  () => { if (drawModeRef.current === "none" && mapRef.current && !dragObstacleRef.current) mapRef.current.getContainer().style.cursor = ""; });
      // Click to select and show measurements + resize handles
      poly.on("click", (e) => {
        L.DomEvent.stopPropagation(e);
        if (drawModeRef.current !== "none") return;
        if (suppressNextClickRef.current) { suppressNextClickRef.current = false; return; } // drag just ended
        setSelectedObsIdx(prev => prev === i ? -1 : i); // toggle selection
      });
      // Right-click for context menu
      poly.on("contextmenu", (e) => {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);
        if (drawModeRef.current !== "none") return;
        setSelectedObsIdx(i);
        setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, type: "obstacle", idx: i });
      });
      // Drag to move: a SELECTED obstacle drags directly; any obstacle drags in Move mode / with Shift
      poly.on("mousedown", (e) => {
        if (drawModeRef.current !== "none") return;
        if (e.originalEvent && e.originalEvent.button !== 0) return;
        if (isSelected || moveModeRef.current || e.originalEvent.shiftKey) {
          L.DomEvent.stopPropagation(e);
          dragObstacleRef.current = { idx: i, startLatLng: e.latlng, poly, pts };
          const m = mapRef.current;
          if (m) m.dragging.disable();
        }
      });
      poly.addTo(obstacleLayerRef.current);
    });
  };

  // ── Context-menu actions (roofs & obstacles) ────────────────────────────────
  const duplicateRoof = (i) => {
    const src = roofPolygons[i];
    const lngs = src.map(p => p.lng);
    const dLng = Math.max(0.00008, (Math.max(...lngs) - Math.min(...lngs)) * 1.1); // paste fully beside (east)
    const dLat = 0.00003;
    const copied = src.map(p => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
    setRoofPolygons(prev => [...prev, copied]);
    setRoofSpecs(prev => [...prev, { ...(roofSpecs[i] || DEFAULT_SPEC) }]);
    markDirty();
    toast.success(`Roof ${i + 1} duplicated`);
  };
  const deleteRoof = (i) => {
    if (roofPolygons.length <= 1) { toast.error("Cannot delete the only roof"); return; }
    setRoofPolygons(prev => prev.filter((_, idx) => idx !== i));
    setRoofSpecs(prev => prev.filter((_, idx) => idx !== i));
    setSelectedRoofIdx(prev => Math.max(0, Math.min(prev, roofPolygons.length - 2)));
    markDirty();
    toast.info(`Roof ${i + 1} deleted`);
  };
  const duplicateObstacle = (i) => {
    const src = obstaclePolygons[i];
    const lngs = src.map(p => p.lng);
    const dLng = Math.max(0.00006, (Math.max(...lngs) - Math.min(...lngs)) * 1.15); // paste fully beside (east)
    const dLat = 0.00002;
    const copied = src.map(p => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
    setObstaclePolygons(prev => [...prev, copied]);
    setObstacleHeights(prev => [...prev, obstacleHeights[i] != null ? obstacleHeights[i] : 1]);
    markDirty();
    toast.success(`${(obstacleHeights[i] != null ? obstacleHeights[i] : 1) <= 0 ? "Keep-out" : "Obstacle"} ${i + 1} duplicated — placed beside the original`);
  };
  const deleteObstacle = (i) => {
    setObstaclePolygons(prev => prev.filter((_, idx) => idx !== i));
    setObstacleHeights(prev => prev.filter((_, idx) => idx !== i));
    setSelectedObsIdx(-1);
    markDirty();
    toast.info(`Obstacle ${i + 1} deleted`);
  };
  const changeObstacleHeight = (i, v) => {
    if (isNaN(v) || v < 0) return;
    setObstacleHeights(prev => { const n = [...prev]; n[i] = v; return n; });
    markDirty();
    toast.success(v <= 0 ? "Set to keep-out (no shadow)" : `Height set to ${v}m`);
  };

  // ── Panel removal (click a panel to drop it from totals) ────────────────────
  const togglePanel = (key) => {
    setRemovedPanels(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    markDirty();
  };

  // Set a roof's azimuth so panel rows run parallel to the clicked edge
  const alignRoofToEdgeAt = (roofIdx, latlng) => {
    const pts = roofPolygonsRef.current[roofIdx];
    if (!pts) return;
    const click = { lat: latlng.lat, lng: latlng.lng };
    const eIdx = nearestEdgeIndex(pts, click);
    const a = pts[eIdx], b = pts[(eIdx + 1) % pts.length];
    const edgeB = bearingDeg(a, b);
    const c1 = (edgeB + 90) % 360, c2 = (edgeB + 270) % 360;
    const distS = (x) => Math.min(Math.abs(x - 180), 360 - Math.abs(x - 180));
    const az = Math.round(distS(c1) <= distS(c2) ? c1 : c2);
    setSelectedRoofIdx(roofIdx);
    setSpec(prev => ({ ...prev, azimuthDeg: az }));
    setRoofSpecs(prev => { const n = [...prev]; n[roofIdx] = { ...(n[roofIdx] || DEFAULT_SPEC), azimuthDeg: az }; return n; });
    setAlignMode(false);
    markDirty();
    toast.success(`Azimuth set to ${az}° — panels aligned parallel to that edge`);
  };

  const applyLayout = (allPanels, totalArea, capped = false) => {
    allPanelsRef.current = allPanels;
    const removed = new Set(removedPanelsRef.current);
    const shown = allPanels.filter(p => !removed.has(panelKey(p)));
    const totalWatts = shown.reduce((s, p) => s + (p.wattW || 0), 0);
    setLayout({
      panels: allPanels,
      totalPanels: shown.length,
      totalKwp: Number((totalWatts / 1000).toFixed(2)),
      totalAreaM2: Math.round(totalArea),
      capped,
    });
    renderPanelLayer(allPanels, removed);
  };

  // Recompute display when the user removes/restores panels (also covers undo)
  useEffect(() => {
    if (!allPanelsRef.current.length) return;
    applyLayout(allPanelsRef.current, layout?.totalAreaM2 || 0, layout?.capped || false);
    renderRowShading(allPanelsRef.current, mapRef.current?.getCenter()?.lat || 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedPanels]);

  // ── Auto-redraw interactive layers when polygons change ─────────────────────
  useEffect(() => {
    if (roofPolygons.length >= 0) redrawRoofLayer(roofPolygons);
    // Show vertex handles on selected roof
    if (selectedRoofIdx >= 0 && roofPolygons[selectedRoofIdx]) {
      showVertexHandles("roof", selectedRoofIdx);
    } else {
      vertexLayerRef.current?.clearLayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofPolygons, selectedRoofIdx, roofSpecs]);   // roofSpecs: ridge / slope arrows follow shape & azimuth

  useEffect(() => {
    if (obstaclePolygons.length >= 0) redrawObstacleLayer(obstaclePolygons);
  }, [obstaclePolygons, obstacleHeights, selectedObsIdx]);

  // Show/hide obstacle measurement labels + vertex handles based on selectedObsIdx
  useEffect(() => {
    const L = window.L;
    if (!L || !obsMeasureLayerRef.current) return;
    obsMeasureLayerRef.current.clearLayers();
    if (selectedObsIdx >= 0 && obstaclePolygons[selectedObsIdx]) {
      const pts = obstaclePolygons[selectedObsIdx];
      for (let j = 0; j < pts.length; j++) {
        const a = pts[j];
        const b = pts[(j + 1) % pts.length];
        createEdgeLabel(L, a, b, "#991B1B").addTo(obsMeasureLayerRef.current);
      }
      showVertexHandles("obstacle", selectedObsIdx);
    } else if (selectedRoofIdx >= 0 && roofPolygons[selectedRoofIdx]) {
      // If no obstacle selected, show roof vertex handles
      showVertexHandles("roof", selectedRoofIdx);
    } else {
      vertexLayerRef.current?.clearLayers();
    }
  }, [selectedObsIdx, obstaclePolygons]);



  // ── Calculate layout ──────────────────────────────────────────────────────
  // Combined shadow polygons for roof i: obstacles + inter-roof (taller roofs) + parapet
  const shadowsForRoof = (i, roofPoly, roofSpec, lat) => {
    const obstacleShadows = computeShadowPolygons(obstaclePolygons, obstacleHeights, lat, roofSpec.azimuthDeg);
    const hi = roofSpec.roofHeightM || 0;
    const interObs = [], interH = [];
    roofPolygons.forEach((rj, j) => {
      if (j === i) return;
      const hj = (roofSpecs[j]?.roofHeightM) || 0;
      if (hj - hi > 0.1) { interObs.push(rj); interH.push(hj - hi); }
    });
    const interShadows = interObs.length ? computeShadowPolygons(interObs, interH, lat, roofSpec.azimuthDeg) : [];
    const parShadows = (roofSpec.parapetHeightM > 0) ? computeParapetShadows(roofPoly, roofSpec.parapetHeightM, lat) : [];
    return {
      all: [...obstacleShadows, ...interShadows, ...parShadows],
      visual: [...obstacleShadows, ...interShadows], // parapet quads are too many to draw
    };
  };

  // Manual calculate button (with toast feedback)
  const calculateWithFeedback = () => {
    if (!roofPolygons.length) { toast.error("Draw at least one roof area first"); return; }
    setCalculating(true);
    setTimeout(() => {
      try {
        const mapCenter = mapRef.current?.getCenter();
        const lat = mapCenter?.lat || 20;
        let allPanels = [];
        let totalArea = 0;
        let totalWatts = 0;
        roofPolygons.forEach((roofPoly, i) => {
          const roofSpec = roofSpecs[i] || spec;
          const shadows = shadowsForRoof(i, roofPoly, roofSpec, lat).all;
          const result = calculateLayout({
            roofPolygons: [roofPoly],
            obstaclePolygons,
            shadowPolygons: shadows,
            ...roofSpec,
          });
          const tagged = result.panels.map(p => ({ ...p, roofIdx: i, wattW: roofSpec.panelWattageW }));
          allPanels = allPanels.concat(tagged);
          totalArea += result.totalAreaM2;
          totalWatts += result.totalPanels * roofSpec.panelWattageW;
        });
        const removed = new Set(removedPanelsRef.current);
        const shown = allPanels.filter(p => !removed.has(panelKey(p)));
        const shownKwp = Number((shown.reduce((s, p) => s + (p.wattW || 0), 0) / 1000).toFixed(2));
        applyLayout(allPanels, totalArea, allPanels.length >= 3000);
        renderRowShading(allPanels, lat);
        if (allPanels.length >= 3000) toast.warning("Very large roof — panel preview was capped. Zoom in and split into segments for an accurate layout.");
        else if (shown.length === 0) toast.warning("No panels fit — try reducing setback or row spacing");
        else toast.success(`${shown.length} panels · ${shownKwp} kWp`);
      } catch (err) { toast.error("Calculation error: " + err.message); }
      setCalculating(false);
    }, 30);
  };

  // ── Auto-recalculate when spec or polygons change (debounced) ─────────────
  const recalcTimerRef = useRef(null);
  useEffect(() => {
    if (!roofPolygons.length) {
      shadowLayerRef.current?.clearLayers();
      panelLayerRef.current?.clearLayers();
      allPanelsRef.current = [];
      setLayout(null);
      return;
    }
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);
    recalcTimerRef.current = setTimeout(() => {
      try {
        const L = window.L;
        const mapCenter = mapRef.current?.getCenter();
        const lat = mapCenter?.lat || 20;
        let allPanels = [];
        let totalArea = 0;
        let totalWatts = 0;
        let allShadows = [];
        let anyCapped = false;
        roofPolygons.forEach((roofPoly, i) => {
          const roofSpec = roofSpecs[i] || spec;
          const sh = shadowsForRoof(i, roofPoly, roofSpec, lat);
          allShadows = allShadows.concat(sh.visual);
          const result = calculateLayout({
            roofPolygons: [roofPoly],
            obstaclePolygons,
            shadowPolygons: sh.all,
            ...roofSpec,
          });
          if (result.capped) anyCapped = true;
          const tagged = result.panels.map(p => ({ ...p, roofIdx: i, wattW: roofSpec.panelWattageW }));
          allPanels = allPanels.concat(tagged);
          totalArea += result.totalAreaM2;
          totalWatts += result.totalPanels * roofSpec.panelWattageW;
        });
        // Render shadow zones visually
        if (L && shadowLayerRef.current) {
          shadowLayerRef.current.clearLayers();
          allShadows.forEach(sp => {
            L.polygon(sp.map(p => [p.lat, p.lng]), {
              color: "#1E293B", fillColor: "#334155", fillOpacity: 0.35,
              weight: 1.2, dashArray: "5 3", interactive: false,
            }).addTo(shadowLayerRef.current);
          });
        }
        applyLayout(allPanels, totalArea, anyCapped);
        renderRowShading(allPanels, lat);
        if (anyCapped && !cappedNotifiedRef.current) {
          cappedNotifiedRef.current = true;
          toast.warning("Very large roof — layout capped for performance. Zoom in and split into segments for an accurate design.");
        }
        if (!anyCapped) cappedNotifiedRef.current = false;
      } catch (err) { console.error(err); }
    }, 150);
    return () => { if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current); };
  }, [spec, roofSpecs, roofPolygons, obstaclePolygons, obstacleHeights]);

  // Inter-row shading overlay (9 am winter solstice — the worst normal case) on the map
  const rowShadeLayerRef = useRef(null);
  const [showRowShade, setShowRowShade] = useState(true);
  const [rowShadeInfo, setRowShadeInfo] = useState(null);
  const showRowShadeRef = useRef(true);
  showRowShadeRef.current = showRowShade;
  const renderRowShading = (allPanels, lat) => {
    const L = window.L;
    if (!L || !mapRef.current) return;
    if (!rowShadeLayerRef.current) rowShadeLayerRef.current = L.layerGroup().addTo(mapRef.current);
    rowShadeLayerRef.current.clearLayers();
    if (!allPanels.length) { setRowShadeInfo(null); return; }
    const sun = sunOffsetAt(lat, -45);
    if (!sun) { setRowShadeInfo(null); return; }
    const removed = new Set(removedPanelsRef.current);
    const o = polygonCentroid(allPanels.map(p => p.center));
    const p3 = [];
    allPanels.forEach(p => {
      const key = panelKey(p);
      if (removed.has(key)) return;
      const rs = roofSpecsRef.current[p.roofIdx] || specRef.current;
      const flush = rs.mountType === "pitched" && (rs.roofPitchDeg || 0) > 0;
      const tilt = flush ? rs.roofPitchDeg : (p.tiltDeg ?? rs.tiltDeg ?? 0);
      const faceDim = rs.orientation === "landscape" ? rs.panelWidthM : rs.panelLengthM;
      const mpg = Math.max(1, Math.round(rs.modulesPerGroup || 1));
      const rise = faceDim * Math.sin(tilt * DEG_RAD);
      const stand = flush ? 0.08 : 0.3;
      const slot = p.tableSlot || 0;
      const zFront = stand + (mpg - 1 - slot) * rise;
      p3.push({ key, roofIdx: p.roofIdx, footprint: p.corners.map(c => latlngToXY(c.lat, c.lng, o.lat, o.lng)), zFront, zBack: zFront + rise });
    });
    const sh = computeRowShading(p3, sun);
    const worst = Math.max(0, ...sh.onPanels.map(x => x.fraction));
    setRowShadeInfo({ shaded: sh.shadedKeys.size, worst, total: p3.length });
    if (!showRowShadeRef.current) return;
    sh.onPanels.forEach(x => {
      L.polygon(x.poly.map(q => { const ll = xyToLatlng(q.x, q.y, o.lat, o.lng); return [ll.lat, ll.lng]; }), {
        color: "#0F172A", fillColor: "#0F172A", fillOpacity: Math.min(0.75, 0.35 + x.fraction * 0.5), weight: 0.5, interactive: false,
      }).addTo(rowShadeLayerRef.current);
    });
  };
  useEffect(() => { if (allPanelsRef.current.length) renderRowShading(allPanelsRef.current, mapRef.current?.getCenter()?.lat || 20); }, [showRowShade]);   // eslint-disable-line react-hooks/exhaustive-deps

  const renderPanelLayer = (panels, removedSet) => {
    const L = window.L;
    if (!L || !panelLayerRef.current) return;
    panelLayerRef.current.clearLayers();
    const removed = removedSet || new Set(removedPanelsRef.current);
    // Panels only intercept clicks when idle — during draw/move the map must receive clicks
    const clickable = drawModeRef.current === "none" && !moveModeRef.current;
    panels.forEach((panel) => {
      const key = panelKey(panel);
      const isRemoved = removed.has(key);
      const poly = L.polygon(panel.corners.map(c => [c.lat, c.lng]), {
        color:       isRemoved ? "#9CA3AF" : PANEL_COLOR,
        fillColor:   isRemoved ? "#D1D5DB" : PANEL_COLOR,
        fillOpacity: isRemoved ? 0.1 : 0.55,
        weight: 1, interactive: clickable, smoothFactor: 0,
        pane: "panelPane",
        dashArray: isRemoved ? "3 3" : null,
      });
      if (clickable) {
        poly.on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          if (alignModeRef.current) { alignRoofToEdgeAt(panel.roofIdx || 0, e.latlng); return; }
          togglePanel(key);
        });
        poly.on("contextmenu", (e) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          if (drawModeRef.current !== "none") return;
          const ri = panel.roofIdx || 0;
          setSelectedRoofIdx(ri);
          setSpec(roofSpecsRef.current[ri] || DEFAULT_SPEC);
          setCtxMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, type: "roof", idx: ri });
        });
        poly.bindTooltip(isRemoved ? "Removed panel — click to restore" : "Click to remove this panel", { sticky: true });
      }
      poly.addTo(panelLayerRef.current);
    });
  };

  // ── Live search — handled by useSolarSearch hook ───────────────────────────

  // ── Save — handled by useSolarSave hook ───────────────────────────────────

  const fetchProjects = async () => {
    try { const { data } = await axios.get(`${API}/projects`); setProjects(data || []); }
    catch (err) { console.error(err); }
  };

  useEffect(() => { fetchProjects(); }, []);

  const loadDesign = (d) => {
    setCurrentDesignId(d.id || d._id || null);
    setDesignName(d.name || "Untitled");
    setClientName(d.client_name || "");
    setSearchQuery(d.location_search || "");
    setModuleBrand(d.module_brand || "");
    const loadedSpec = { ...DEFAULT_SPEC, ...(d.active_spec || d.spec || {}) };
    setSpec(loadedSpec);

    const polys    = d.roof_polygons || [];
    const obsPolys = d.obstacle_polygons || [];
    const obsHeights = d.obstacle_heights || obsPolys.map(() => 1.0);
    // Load per-roof specs (fallback: all roofs use the global spec)
    const perRoofSpecs = (d.roof_specs && d.roof_specs.length)
      ? d.roof_specs.map(s => ({ ...DEFAULT_SPEC, ...s }))
      : polys.map(() => ({ ...DEFAULT_SPEC, ...loadedSpec }));
    setRoofPolygons(polys);
    setRoofSpecs(perRoofSpecs);
    setObstaclePolygons(obsPolys);
    setObstacleHeights(obsHeights);
    setSelectedRoofIdx(0);
    if (perRoofSpecs[0]) setSpec(perRoofSpecs[0]);

    const loadedRemoved = d.removed_panels || [];
    removedPanelsRef.current = loadedRemoved;
    setRemovedPanels(loadedRemoved);
    if (d.specific_yield != null) setSpecificYield(d.specific_yield);
    if (d.dc_ac_ratio != null) setDcAcRatio(d.dc_ac_ratio);
    // Reset undo/redo history for a freshly loaded design
    undoRef.current = []; redoRef.current = []; prevSnapRef.current = null;

    const map = mapRef.current;
    if (map) {
      if (d.map_center) map.setView([d.map_center.lat, d.map_center.lng], d.map_zoom || 19);
      redrawRoofLayer(polys);
      redrawObstacleLayer(obsPolys);
    }

    // Recalculate using per-roof specs (auto-recalc effect will also trigger)
    if (polys.length) {
      try {
        const lat = d.map_center?.lat || 20;
        let allPanels = [];
        let totalArea = 0;
        let totalWatts = 0;
        polys.forEach((roofPoly, i) => {
          const rSpec = perRoofSpecs[i] || loadedSpec;
          const shadows = computeShadowPolygons(obsPolys, obsHeights, lat, rSpec.azimuthDeg);
          const result = calculateLayout({ roofPolygons: [roofPoly], obstaclePolygons: obsPolys, shadowPolygons: shadows, ...rSpec });
          const tagged = result.panels.map(p => ({ ...p, roofIdx: i, wattW: rSpec.panelWattageW }));
          allPanels = allPanels.concat(tagged);
          totalArea += result.totalAreaM2;
          totalWatts += result.totalPanels * rSpec.panelWattageW;
        });
        applyLayout(allPanels, totalArea);
      } catch (err) { console.error(err); }
    } else {
      setLayout(null);
      panelLayerRef.current?.clearLayers();
    }

    setShowSaved(false);
    setHasUnsaved(false);
    toast.success(`Loaded: ${d.name}`);
  };

  // ── PDF Export ────────────────────────────────────────────────────────────
  const exportPDF = async () => {
    if (!layout) { toast.error("Calculate layout first"); return; }
    setExporting(true);
    toast.info("Generating PDF…");
    try {
      await exportSolarPDF({
        mapContainerRef, mapRef, layout, spec,
        roofPolygons, roofSpecs, obstaclePolygons, obstacleHeights,
        removedKeys: new Set(removedPanelsRef.current),
        designName, clientName, searchQuery, moduleBrand,
        annualKwh, inverterAcKw,
      });
      toast.success("PDF downloaded!");
    } catch (err) { toast.error("PDF export failed: " + err.message); }
    setExporting(false);
  };

  // ── Spec change handler — updates selected roof's spec ─────────────────────
  const handleSpecChange = useCallback((field, value) => {
    setSpec(prev => ({ ...prev, [field]: value }));
    // Also update the per-roof spec for the selected segment
    setRoofSpecs(prev => {
      if (!prev.length) return prev;
      const next = [...prev];
      const idx = Math.min(selectedRoofIdx, next.length - 1);
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    markDirty();
  }, [selectedRoofIdx]);

  const ctxItemStyle = { display: "flex", alignItems: "center", gap: "8px", width: "100%", background: "none", border: "none", textAlign: "left", padding: "8px", borderRadius: "7px", fontSize: "12.5px", fontWeight: 600, color: "#1B2B22", cursor: "pointer", fontFamily: "Manrope, sans-serif" };

  // Per-roof panel/kWp stats (respecting removed panels) for the segment list badges
  const removedKeySet = useMemo(() => new Set(removedPanels), [removedPanels]);
  const removedSet = removedKeySet;
  const perRoofStats = {};
  (layout?.panels || []).forEach(p => {
    if (removedSet.has(panelKey(p))) return;
    if (!perRoofStats[p.roofIdx]) perRoofStats[p.roofIdx] = { count: 0, watt: 0 };
    perRoofStats[p.roofIdx].count++;
    perRoofStats[p.roofIdx].watt += (p.wattW || 0);
  });

  // Energy & system estimates
  const annualKwh = layout ? Math.round(layout.totalKwp * specificYield) : 0;
  const inverterAcKw = layout ? Number((layout.totalKwp / (dcAcRatio || 1.2)).toFixed(1)) : 0;

  return (
    <div style={{ display: "flex", height: "calc(100vh - 64px)", overflow: "hidden", background: "#F5F8F5" }}>

      {/* ── Map area ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: "relative" }}>

        {/* Search */}
        <div style={{ position: "absolute", top: "12px", left: "12px", zIndex: 1000, display: "flex", gap: "8px", width: "340px" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <input
              value={searchQuery}
              onChange={e => handleSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submitSearch(); } }}
              placeholder="Search place / address, or paste coordinates or a Google Maps link"
              style={{ width: "100%", background: "rgba(255,255,255,0.96)", backdropFilter: "blur(8px)", border: "1px solid rgba(46,100,68,0.15)", borderRadius: "10px", padding: "10px 36px 10px 36px", fontSize: "13px", color: "#1B2B22", outline: "none", boxShadow: "0 2px 12px rgba(27,43,34,0.12)", fontFamily: "Manrope, sans-serif" }}
              data-testid="location-search"
            />
            <Search size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#8AA496", pointerEvents: "none" }} />
            {searchLoading && (
              <div style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", width: "14px", height: "14px", border: "2px solid rgba(47,125,82,0.3)", borderTop: "2px solid #2F7D52", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            )}
          </div>

          {searchResults.length > 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: "340px", background: "#FFFFFF", border: "1px solid rgba(46,100,68,0.12)", borderRadius: "10px", boxShadow: "0 8px 24px rgba(27,43,34,0.14)", zIndex: 2000, overflow: "hidden" }}>
              {searchResults.map((r, i) => (
                <button key={r.place_id || `sr-${i}`} onClick={() => goToLocation(r)}
                  style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 14px", fontSize: "12px", color: "#1B2B22", cursor: "pointer", borderBottom: i < searchResults.length - 1 ? "1px solid rgba(46,100,68,0.07)" : "none", fontFamily: "Manrope, sans-serif" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#F0F7F2"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
                >
                  <MapPin size={10} style={{ marginRight: "6px", color: "#2F7D52", display: "inline" }} />
                  {r.display_name.substring(0, 60)}{r.display_name.length > 60 ? "…" : ""}
                </button>
              ))}
              <button onClick={() => setSearchResults([])} style={{ display: "block", width: "100%", background: "#F5F8F5", border: "none", borderTop: "1px solid rgba(46,100,68,0.08)", padding: "7px", fontSize: "11px", color: "#8AA496", cursor: "pointer", fontFamily: "Manrope, sans-serif" }}>
                Close
              </button>
            </div>
          )}
        </div>

        {/* Draw toolbar */}
        <div data-testid="draw-toolbar" style={{ position: "absolute", top: "62px", left: "50%", transform: "translateX(-50%)", zIndex: 1000, display: "flex", flexWrap: "wrap", justifyContent: "center", maxWidth: "calc(100% - 24px)", gap: "6px", background: "rgba(255,255,255,0.96)", backdropFilter: "blur(8px)", border: "1px solid rgba(46,100,68,0.12)", borderRadius: "12px", padding: "6px 8px", boxShadow: "0 2px 12px rgba(27,43,34,0.1)" }}>
          <button
            onClick={() => drawMode === "roof" ? (drawPointCount > 0 ? finishPolygon() : stopDraw()) : startDraw("roof")}
            data-testid="draw-roof-btn"
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: drawMode === "roof" ? "#FEF3C7" : "#F0F7F2", color: drawMode === "roof" ? "#92400E" : "#2F7D52" }}
          >
            <Pencil size={13} />
            {drawMode === "roof" ? (drawPointCount > 0 ? `Close (${drawPointCount} pts)` : "Cancel") : "Draw Roof"}
          </button>
          <button
            onClick={() => drawMode === "rect" ? stopDraw() : startDraw("rect")}
            data-testid="draw-rect-btn"
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: drawMode === "rect" ? "#FEF3C7" : "#F0F7F2", color: drawMode === "rect" ? "#92400E" : "#2F7D52" }}
          >
            <Square size={13} />
            {drawMode === "rect" ? "Cancel" : "Rectangle"}
          </button>
          <button
            onClick={() => { if (drawMode !== "none") stopDraw(); setDimsDialog({ type: "new-roof" }); }}
            data-testid="roof-by-size-btn"
            title="Add a roof by typing its width × length (placed at the map centre, then drag it into place)"
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: "#F0F7F2", color: "#2F7D52" }}
          >
            <Ruler size={13} />
            By size
          </button>
          <button
            onClick={() => drawMode === "obstacle" ? (drawPointCount > 0 ? finishPolygon() : stopDraw()) : startDraw("obstacle")}
            data-testid="draw-obstacle-btn"
            title="Trace an obstacle or keep-out area. Height > 0 casts a shadow; 'No shadow' reserves space only (walkway, skylight)."
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: drawMode === "obstacle" ? "#FEE2E2" : "#F5F8F5", color: drawMode === "obstacle" ? "#991B1B" : "#5A7264" }}
          >
            <Slash size={13} />
            {drawMode === "obstacle" ? (drawPointCount > 0 ? `Close (${drawPointCount} pts)` : "Cancel") : "Mark Obstacle"}
          </button>
          <button
            onClick={() => { setMoveMode(!moveMode); if (drawMode !== "none") stopDraw(); }}
            data-testid="move-mode-btn"
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: moveMode ? "2px solid #2563EB" : "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: moveMode ? "#DBEAFE" : "#F5F8F5", color: moveMode ? "#1D4ED8" : "#5A7264" }}
          >
            <MapPin size={13} />
            {moveMode ? "Moving…" : "Move"}
          </button>
          <button
            onClick={() => { setAlignMode(a => !a); if (drawMode !== "none") stopDraw(); setMoveMode(false); }}
            data-testid="align-azimuth-btn"
            disabled={!roofPolygons.length}
            title="Click a roof edge to orient panel rows parallel to it"
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: alignMode ? "2px solid #D97706" : "none", cursor: roofPolygons.length ? "pointer" : "not-allowed", opacity: roofPolygons.length ? 1 : 0.5, fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: alignMode ? "#FEF3C7" : "#F5F8F5", color: alignMode ? "#92400E" : "#5A7264" }}
          >
            <Compass size={13} />
            {alignMode ? "Click an edge…" : "Align"}
          </button>
          <button
            onClick={() => { if (drawMode !== "none") stopDraw(); setShow3D(true); }}
            data-testid="view-3d-btn"
            disabled={!roofPolygons.length}
            title="See the roofs, obstacles and tilted panels in 3D"
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", borderRadius: "8px", border: "none", cursor: roofPolygons.length ? "pointer" : "not-allowed", opacity: roofPolygons.length ? 1 : 0.5, fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", background: "#0A1128", color: "#FFB703" }}
          >
            <Box size={13} />
            3D
          </button>
          <div style={{ width: "1px", background: "rgba(46,100,68,0.12)", margin: "2px 2px" }} />
          <button onClick={undo} disabled={!undoRef.current.length} data-testid="undo-btn" title="Undo (Ctrl+Z)"
            style={{ display: "flex", alignItems: "center", padding: "7px 9px", borderRadius: "8px", border: "none", cursor: undoRef.current.length ? "pointer" : "not-allowed", opacity: undoRef.current.length ? 1 : 0.4, background: "#F5F8F5", color: "#5A7264" }}>
            <Undo2 size={14} />
          </button>
          <button onClick={redo} disabled={!redoRef.current.length} data-testid="redo-btn" title="Redo (Ctrl+Y)"
            style={{ display: "flex", alignItems: "center", padding: "7px 9px", borderRadius: "8px", border: "none", cursor: redoRef.current.length ? "pointer" : "not-allowed", opacity: redoRef.current.length ? 1 : 0.4, background: "#F5F8F5", color: "#5A7264" }}>
            <Redo2 size={14} />
          </button>
          {(roofPolygons.length > 0 || obstaclePolygons.length > 0 || layout) && (
            <button onClick={clearAll} title="Clear everything" style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 10px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "11px", fontWeight: "600", background: "#FEF2F2", color: "#DC2626", fontFamily: "Manrope, sans-serif" }}>
              <Trash2 size={12} /> Clear
            </button>
          )}
        </div>

        {/* Drawing progress hint */}
        {drawMode !== "none" && drawPointCount > 0 && (
          <div style={{ position: "absolute", bottom: "16px", left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: "rgba(27,43,34,0.85)", color: "white", borderRadius: "100px", padding: "7px 16px", fontSize: "12px", fontWeight: "600", fontFamily: "Manrope, sans-serif", backdropFilter: "blur(6px)", pointerEvents: "none" }}>
            {drawPointCount} point{drawPointCount !== 1 ? "s" : ""} — double-click or click "Close" to finish
          </div>
        )}

        {/* Legend */}
        <div style={{ position: "absolute", bottom: "12px", right: "12px", zIndex: 1000, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "10px", padding: "10px 12px", fontSize: "11px", fontFamily: "Manrope, sans-serif" }}>
          {layout && (
            <label style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", color: rowShadeInfo?.shaded ? "#B45309" : "#1B2B22", fontWeight: 600, cursor: "pointer" }} data-testid="row-shade-toggle">
              <input type="checkbox" checked={showRowShade} onChange={e => setShowRowShade(e.target.checked)} />
              Row shading 9 am winter{rowShadeInfo ? (rowShadeInfo.shaded ? `: ${rowShadeInfo.shaded} panel${rowShadeInfo.shaded > 1 ? "s" : ""} (worst ${Math.round(rowShadeInfo.worst * 100)}%)` : ": none") : ""}
            </label>
          )}
          {[["rgba(245,158,11,0.5)", "#F59E0B", "Roof area"], ["rgba(239,68,68,0.4)", "#EF4444", "Obstacle"], ["rgba(107,114,128,0.3)", "#6B7280", "Obstacle (no shadow)"], ["rgba(51,65,85,0.35)", "#1E293B", "Shadow"], ["rgba(59,130,246,0.55)", "#3B82F6", "Solar panel"]].map(([bg, border, label]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", color: "#1B2B22" }}>
              <span style={{ width: "12px", height: "8px", background: bg, border: `1px solid ${border}`, borderRadius: "2px", display: "inline-block" }} />
              {label}
            </div>
          ))}
        </div>

        {/* Right-click context menu (roofs & obstacles) */}
        {ctxMenu && (
          <>
            <div onMouseDown={() => setCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
              style={{ position: "fixed", inset: 0, zIndex: 3000 }} />
            <div data-testid="context-menu" style={{ position: "fixed", top: Math.min(ctxMenu.y, window.innerHeight - 180), left: Math.min(ctxMenu.x, window.innerWidth - 190), zIndex: 3001, background: "#fff", border: "1px solid rgba(46,100,68,0.15)", borderRadius: "10px", boxShadow: "0 8px 28px rgba(27,43,34,0.22)", padding: "6px", minWidth: "172px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#8AA496", padding: "5px 8px 6px" }}>
                {ctxMenu.type === "roof" ? `Roof ${ctxMenu.idx + 1}` : `Obstacle ${ctxMenu.idx + 1}`}
              </div>
              <button data-testid="ctx-dimensions" style={ctxItemStyle}
                onMouseEnter={e => e.currentTarget.style.background = "#F0F7F2"} onMouseLeave={e => e.currentTarget.style.background = "none"}
                onClick={() => { setDimsDialog({ type: ctxMenu.type, idx: ctxMenu.idx }); setCtxMenu(null); }}>
                <Ruler size={13} /> Set dimensions…
              </button>
              <button data-testid="ctx-duplicate" style={ctxItemStyle}
                onMouseEnter={e => e.currentTarget.style.background = "#F0F7F2"} onMouseLeave={e => e.currentTarget.style.background = "none"}
                onClick={() => { ctxMenu.type === "roof" ? duplicateRoof(ctxMenu.idx) : duplicateObstacle(ctxMenu.idx); setCtxMenu(null); }}>
                <Copy size={13} /> Duplicate
              </button>
              {ctxMenu.type === "obstacle" && (
                <button data-testid="ctx-set-height" style={ctxItemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = "#F0F7F2"} onMouseLeave={e => e.currentTarget.style.background = "none"}
                  onClick={() => { const raw = window.prompt("Obstacle height in metres (0 = no shadow / keep-out):", obstacleHeights[ctxMenu.idx] ?? 1); const v = parseFloat(raw); if (!isNaN(v)) changeObstacleHeight(ctxMenu.idx, v); setCtxMenu(null); }}>
                  <Ruler size={13} /> Set height…
                </button>
              )}
              <div style={{ height: "1px", background: "rgba(46,100,68,0.1)", margin: "4px 2px" }} />
              <button data-testid="ctx-delete" style={{ ...ctxItemStyle, color: "#DC2626" }}
                onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"} onMouseLeave={e => e.currentTarget.style.background = "none"}
                onClick={() => { ctxMenu.type === "roof" ? deleteRoof(ctxMenu.idx) : deleteObstacle(ctxMenu.idx); setCtxMenu(null); }}>
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </>
        )}

        <Layout3DModal
          open={show3D}
          onClose={() => setShow3D(false)}
          roofPolygons={roofPolygons}
          roofSpecs={roofSpecs}
          obstaclePolygons={obstaclePolygons}
          obstacleHeights={obstacleHeights}
          panels={layout?.panels || []}
          removedKeys={removedKeySet}
          spec={roofSpecs[selectedRoofIdx] || spec}
          designName={designName}
          totals={layout}
          latitude={mapRef.current?.getCenter()?.lat}
        />

        {dimsDialog && (
          <DimensionsDialog
            req={dimsDialog}
            roofPolygons={roofPolygons}
            obstaclePolygons={obstaclePolygons}
            mapRef={mapRef}
            onClose={() => setDimsDialog(null)}
            onApply={(pts) => {
              const t = dimsDialog.type;
              if (t === "roof") { setRoofPolygons(prev => prev.map((p, i) => i === dimsDialog.idx ? pts : p)); }
              else if (t === "obstacle") { setObstaclePolygons(prev => prev.map((p, i) => i === dimsDialog.idx ? pts : p)); }
              else if (t === "new-roof") { addRoofPolygon(pts); }
              else if (t === "new-obstacle") { setObstaclePolygons(prev => [...prev, pts]); setObstacleHeights(prev => [...prev, 1.0]); setSelectedObsIdx(obstaclePolygonsRef.current.length); }
              markDirty();
              setDimsDialog(null);
              toast.success(t.startsWith("new") ? "Shape added at the map centre — drag it into place" : "Dimensions applied");
            }}
          />
        )}

        {/* Panel-editing tip */}
        {layout && layout.panels && layout.panels.length > 0 && drawMode === "none" && !alignMode && (
          <div style={{ position: "absolute", bottom: "16px", left: "12px", zIndex: 1000, background: "rgba(59,130,246,0.92)", color: "white", borderRadius: "100px", padding: "6px 14px", fontSize: "11px", fontWeight: 600, fontFamily: "Manrope, sans-serif", backdropFilter: "blur(6px)", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
            Tip: click any panel to remove it · right-click a shape for options
          </div>
        )}

        {/* Leaflet map — NOTE: no className prop, Leaflet manages its own classes */}
        <div
          ref={mapContainerRef}
          id="solar-map"
          data-testid="map-container"
          onContextMenu={e => e.preventDefault()}
          style={{ position: "absolute", inset: 0, background: "#E8F0E9" }}
        />
      </div>

      {/* ── Right panel ───────────────────────────────────────────────── */}
      <div style={{ width: "300px", flexShrink: 0, background: "#FFFFFF", borderLeft: "1px solid rgba(46,100,68,0.1)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid rgba(46,100,68,0.08)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#FFB703", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sun size={14} style={{ color: "#1B2B22" }} />
            </div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#1B2B22", fontFamily: "Outfit, sans-serif" }}>Solar Designer</div>
              <div style={{ fontSize: "10px", color: "#8AA496" }}>Preliminary layout tool</div>
            </div>
            {hasUnsaved && (
              <span style={{ marginLeft: "auto", fontSize: "9px", fontWeight: "700", color: "#D97706", background: "#FEF3C7", border: "1px solid rgba(217,119,6,0.2)", borderRadius: "100px", padding: "2px 7px" }}>UNSAVED</span>
            )}
          </div>
          <input value={designName} onChange={e => { setDesignName(e.target.value); markDirty(); }} placeholder="Design name…" style={{ width: "100%", background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.12)", borderRadius: "8px", padding: "8px 10px", fontSize: "12px", color: "#1B2B22", outline: "none", fontFamily: "Manrope, sans-serif" }} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.4)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.12)"; }} data-testid="design-name" />
          <input value={clientName} onChange={e => { setClientName(e.target.value); markDirty(); }} placeholder="Client name (for PDF)…" style={{ width: "100%", marginTop: "6px", background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.12)", borderRadius: "8px", padding: "8px 10px", fontSize: "12px", color: "#1B2B22", outline: "none", fontFamily: "Manrope, sans-serif" }} onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.4)"; }} onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.12)"; }} data-testid="client-name" />
          {/* Link to project */}
          <select
            value={linkedProjectId}
            onChange={e => { setLinkedProjectId(e.target.value); markDirty(); }}
            style={{ width: "100%", marginTop: "6px", background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.12)", borderRadius: "8px", padding: "8px 10px", fontSize: "12px", color: "#1B2B22", outline: "none", fontFamily: "Manrope, sans-serif", cursor: "pointer" }}
            data-testid="project-link-select"
          >
            <option value="">— Link to project (optional) —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px" }}>

          {/* Roof segment selector */}
          {roofPolygons.length > 1 && (
            <div style={{ marginBottom: "14px", padding: "10px", background: "#F0F7F2", borderRadius: "9px", border: "1px solid rgba(46,100,68,0.15)" }}>
              <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "6px" }}>Editing Roof Segment</div>
              <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                {roofPolygons.map((_, i) => (
                  <button key={i} onClick={() => { setSelectedRoofIdx(i); setSpec(roofSpecs[i] || DEFAULT_SPEC); redrawRoofLayer(roofPolygons); }}
                    data-testid={`roof-segment-${i}`}
                    style={{ padding: "5px 10px", borderRadius: "6px", border: selectedRoofIdx === i ? "1.5px solid #2F7D52" : "1px solid rgba(46,100,68,0.15)", background: selectedRoofIdx === i ? "#E6F4EC" : "#FFF", color: selectedRoofIdx === i ? "#2F7D52" : "#5A7264", fontSize: "11px", fontWeight: "600", cursor: "pointer", fontFamily: "Manrope, sans-serif" }}>
                    Roof {i + 1}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: "10px", color: "#5A7264", marginTop: "5px" }}>
                Each roof can have different specs. Click a roof on the map or use buttons above.
              </div>
            </div>
          )}

          {/* Panel specs */}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "8px" }}>Panel Specs {roofPolygons.length > 1 ? `(Roof ${selectedRoofIdx + 1})` : ""}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <SpecInput label="Length"  field="panelLengthM"  unit="m"  min={0.5} max={3}   step={0.05} spec={spec} onSpecChange={handleSpecChange} />
              <SpecInput label="Width"   field="panelWidthM"   unit="m"  min={0.3} max={2}   step={0.05} spec={spec} onSpecChange={handleSpecChange} />
              <SpecInput label="Wattage" field="panelWattageW" unit="Wp" min={100} max={800} step={5}    spec={spec} onSpecChange={handleSpecChange} />
            </div>
            {/* Module Brand */}
            <div style={{ marginTop: "8px" }}>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#8AA496", marginBottom: "4px" }}>Module Brand / Model</label>
              <input
                value={moduleBrand}
                onChange={e => { setModuleBrand(e.target.value); markDirty(); }}
                placeholder="e.g. Adani 540Wp Mono PERC"
                style={{ width: "100%", background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.15)", borderRadius: "7px", padding: "7px 8px", fontSize: "12px", color: "#1B2B22", outline: "none", fontFamily: "Manrope, sans-serif" }}
                onFocus={e => { e.target.style.borderColor = "rgba(47,125,82,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(47,125,82,0.08)"; }}
                onBlur={e => { e.target.style.borderColor = "rgba(46,100,68,0.15)"; e.target.style.boxShadow = "none"; }}
                data-testid="module-brand"
              />
            </div>
            {/* Orientation toggle */}
            <div style={{ marginTop: "8px" }}>
              <label style={{ display: "block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#8AA496", marginBottom: "4px" }}>Orientation</label>
              <div style={{ display: "flex", gap: "4px" }}>
                {["portrait", "landscape"].map(o => (
                  <button key={o} onClick={() => { handleSpecChange("orientation", o); }}
                    data-testid={`orientation-${o}`}
                    style={{ flex: 1, padding: "7px 0", borderRadius: "7px", border: spec.orientation === o ? "1.5px solid #2F7D52" : "1px solid rgba(46,100,68,0.15)", background: spec.orientation === o ? "#E6F4EC" : "#F5F8F5", color: spec.orientation === o ? "#2F7D52" : "#5A7264", fontSize: "11px", fontWeight: "600", cursor: "pointer", fontFamily: "Manrope, sans-serif", textTransform: "capitalize" }}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Layout settings */}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "8px" }}>Layout Settings</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <SpecInput label="Azimuth" field="azimuthDeg"        unit="°N" min={0} max={360} step={1}    spec={spec} onSpecChange={handleSpecChange} />
              <SpecInput label="Tilt"    field="tiltDeg"           unit="°"  min={0} max={45}  step={0.5}  spec={spec} onSpecChange={handleSpecChange} />
              <SpecInput label="Row Gap" field="interRowSpacingM"  unit="m"  min={0} max={5}   step={0.05} spec={spec} onSpecChange={handleSpecChange} />
              <SpecInput label="Setback" field="setbackM"          unit="m"  min={0} max={3}   step={0.1}  spec={spec} onSpecChange={handleSpecChange} />
            </div>
            <div style={{ marginTop: "8px", padding: "8px 10px", background: "#F0F7F2", borderRadius: "7px", fontSize: "11px", color: "#5A7264", lineHeight: 1.5 }}>
              <Info size={11} style={{ marginRight: "4px", display: "inline", verticalAlign: "middle" }} />
              180° = South-facing (ideal for India). Row gap added after each panel row.
            </div>
          </div>

          {/* Roof & structure (per selected segment) */}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "8px" }}>Roof &amp; Structure {roofPolygons.length > 1 ? `· Roof ${selectedRoofIdx + 1}` : ""}</div>
            <label style={{ display: "block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#8AA496", marginBottom: "4px" }}>Mounting</label>
            <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
              {[["flat", "Flat (tilted)"], ["pitched", "Sloped (flush)"]].map(([val, lbl]) => (
                <button key={val} onClick={() => { handleSpecChange("mountType", val); if (val === "pitched" && (spec.interRowSpacingM ?? 0.8) >= 0.5) handleSpecChange("interRowSpacingM", 0.1); if (val === "pitched" && !(spec.roofPitchDeg > 0)) { handleSpecChange("roofShape", spec.roofShape && spec.roofShape !== "flat" ? spec.roofShape : "mono"); handleSpecChange("roofPitchDeg", spec.tiltDeg || 10); } }}
                  data-testid={`mount-${val}`}
                  style={{ flex: 1, padding: "7px 0", borderRadius: "7px", border: (spec.mountType || "flat") === val ? "1.5px solid #2F7D52" : "1px solid rgba(46,100,68,0.15)", background: (spec.mountType || "flat") === val ? "#E6F4EC" : "#F5F8F5", color: (spec.mountType || "flat") === val ? "#2F7D52" : "#5A7264", fontSize: "11px", fontWeight: "600", cursor: "pointer", fontFamily: "Manrope, sans-serif" }}>
                  {lbl}
                </button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <SpecInput label="Roof Height" field="roofHeightM"    unit="m" min={0} max={100} step={0.5} spec={spec} onSpecChange={handleSpecChange} />
              <SpecInput label="Parapet"     field="parapetHeightM" unit="m" min={0} max={3}   step={0.1} spec={spec} onSpecChange={handleSpecChange} />
            </div>
            {(spec.mountType || "flat") === "pitched" && (
              <div style={{ marginTop: "8px" }} data-testid="roof-shape-controls">
                <label style={{ display: "block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#8AA496", marginBottom: "4px" }}>Roof shape</label>
                <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
                  {[["flat", "Flat"], ["mono", "One slope"], ["gable", "Two-sided"]].map(([val, lbl]) => (
                    <button key={val} onClick={() => handleSpecChange("roofShape", val)} data-testid={`roof-shape-${val}`}
                      style={{ flex: 1, padding: "6px 0", borderRadius: "7px", border: (spec.roofShape || "flat") === val ? "1.5px solid #2F7D52" : "1px solid rgba(46,100,68,0.15)", background: (spec.roofShape || "flat") === val ? "#E6F4EC" : "#F5F8F5", color: (spec.roofShape || "flat") === val ? "#2F7D52" : "#5A7264", fontSize: "11px", fontWeight: "600", cursor: "pointer", fontFamily: "Manrope, sans-serif" }}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {(spec.roofShape || "flat") !== "flat" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <SpecInput label="Roof pitch" field="roofPitchDeg" unit="°" min={0} max={45} step={0.5} spec={spec} onSpecChange={handleSpecChange} />
                    <div style={{ fontSize: "10.5px", color: "#5A7264", alignSelf: "end", paddingBottom: "6px" }}>
                      Slopes down toward <b>{spec.azimuthDeg}°</b>{(spec.roofShape === "gable") ? " and the opposite side" : ""} — change Azimuth (or Align) to rotate.
                    </div>
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: "8px", padding: "8px 10px", background: "#F0F7F2", borderRadius: "7px", fontSize: "11px", color: "#5A7264", lineHeight: 1.5 }}>
              <Info size={11} style={{ marginRight: "4px", display: "inline", verticalAlign: "middle" }} />
              Roof Height sets this segment's level — taller segments cast shade on lower ones. Parapet shades panels near the edges. In Sloped mode modules lie flush at the roof pitch and the Row Gap is a maintenance gap (try 0.05–0.3 m).
            </div>
          </div>

          {/* Module grouping (table configuration) */}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "8px" }}>Table Configuration</div>
            <SpecInput label="Modules per Table" field="modulesPerGroup" unit="nos" min={1} max={6} step={1} spec={spec} onSpecChange={handleSpecChange} />
            <div style={{ marginTop: "6px", padding: "8px 10px", background: "#F0F7F2", borderRadius: "7px", fontSize: "11px", color: "#5A7264", lineHeight: 1.5 }}>
              <Info size={11} style={{ marginRight: "4px", display: "inline", verticalAlign: "middle" }} />
              Modules stacked in tilt direction per table (e.g. 2 = 2P configuration). Row Gap applies between tables.
            </div>
          </div>

          {/* Energy & system */}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "8px" }}>Energy &amp; System</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <label style={{ display: "block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#8AA496", marginBottom: "4px" }}>Specific Yield</label>
                <div style={{ position: "relative" }}>
                  <input type="number" value={specificYield} min={800} max={2200} step={10}
                    onChange={e => setSpecificYield(Number(e.target.value) || 0)}
                    data-testid="specific-yield-input"
                    style={{ width: "100%", background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.15)", borderRadius: "7px", padding: "8px 44px 8px 10px", fontSize: "13px", color: "#1B2B22", outline: "none", fontFamily: "Manrope, sans-serif" }} />
                  <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#8AA496" }}>kWh/kWp</span>
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "10px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#8AA496", marginBottom: "4px" }}>DC:AC Ratio</label>
                <div style={{ position: "relative" }}>
                  <input type="number" value={dcAcRatio} min={1} max={1.6} step={0.05}
                    onChange={e => setDcAcRatio(Number(e.target.value) || 1.2)}
                    data-testid="dc-ac-input"
                    style={{ width: "100%", background: "#F5F8F5", border: "1px solid rgba(46,100,68,0.15)", borderRadius: "7px", padding: "8px 10px", fontSize: "13px", color: "#1B2B22", outline: "none", fontFamily: "Manrope, sans-serif" }} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: "6px", padding: "8px 10px", background: "#F0F7F2", borderRadius: "7px", fontSize: "11px", color: "#5A7264", lineHeight: 1.5 }}>
              <Info size={11} style={{ marginRight: "4px", display: "inline", verticalAlign: "middle" }} />
              India specific yield is typically 1400–1600 kWh/kWp/yr. Annual generation &amp; recommended inverter size appear in results.
            </div>
          </div>

          {/* Obstacles Management */}
          {obstaclePolygons.length > 0 && (
            <div style={{ marginBottom: "14px", padding: "10px", background: "#FFF5F5", borderRadius: "9px", border: "1px solid rgba(239,68,68,0.15)" }} data-testid="obstacle-list">
              <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#DC2626", marginBottom: "8px" }}>Obstacles ({obstaclePolygons.length})</div>
              {obstaclePolygons.map((_, i) => {
                const h = obstacleHeights[i] ?? 1;
                const noShadow = h <= 0;
                const sel = selectedObsIdx === i;
                return (
                  <div key={i} data-testid={`obstacle-row-${i}`}
                    onClick={() => setSelectedObsIdx(sel ? -1 : i)}
                    style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "5px", padding: "5px 6px", cursor: "pointer",
                      background: sel ? (noShadow ? "#F3F4F6" : "#FEE2E2") : "#FFF", borderRadius: "6px",
                      border: sel ? `1.5px solid ${noShadow ? "#374151" : "#DC2626"}` : "1px solid rgba(239,68,68,0.1)" }}>
                    <span style={{ fontSize: "11px", fontWeight: sel ? "800" : "600", color: noShadow ? "#374151" : "#7F1D1D", minWidth: "44px" }}>
                      {noShadow ? "Keep-out" : "Obs"} {i + 1}{sel ? " ✓" : ""}
                    </span>
                    <input type="number" min={0} max={50} step={0.5}
                      value={h}
                      disabled={noShadow}
                      onClick={e => e.stopPropagation()}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0) {
                          setObstacleHeights(prev => { const next = [...prev]; next[i] = v; return next; });
                          markDirty();
                        }
                      }}
                      style={{ width: "46px", background: noShadow ? "#F3F4F6" : "#FFF5F5", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "5px", padding: "4px 5px", fontSize: "11px", textAlign: "center", opacity: noShadow ? 0.5 : 1 }}
                      data-testid={`obstacle-height-${i}`}
                      title={noShadow ? "No shadow — untick to set a height" : "Height in metres (drives the winter-solstice shadow)"}
                    />
                    <span style={{ fontSize: "9px", color: "#DC2626" }}>m</span>
                    <label onClick={e => e.stopPropagation()} title="Reserve space only (walkway, skylight, access path) — panels are excluded but no shadow is cast"
                      style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "9.5px", color: "#374151", cursor: "pointer", whiteSpace: "nowrap" }}>
                      <input type="checkbox" checked={noShadow} data-testid={`obstacle-noshadow-${i}`}
                        onChange={e => {
                          const v = e.target.checked ? 0 : 1.0;
                          setObstacleHeights(prev => { const next = [...prev]; next[i] = v; return next; });
                          markDirty();
                        }} style={{ margin: 0 }} />
                      No shadow
                    </label>
                    <button onClick={e => { e.stopPropagation(); duplicateObstacle(i); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#2F7D52", fontSize: "11px", padding: "2px" }} title="Copy (placed beside the original)">
                      Copy
                    </button>
                    <button onClick={e => { e.stopPropagation(); deleteObstacle(i); }} data-testid={`obstacle-delete-${i}`}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: "11px", padding: "2px" }} title="Delete (or select and press Delete)">
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
              <div style={{ fontSize: "9px", color: "#7F1D1D", marginTop: "4px", lineHeight: 1.4 }}>
                Click a row or shape to select it, then drag it on the map to move, drag its corners to reshape, or press Delete to remove.
                Height creates a winter-solstice shadow zone; “No shadow” only reserves the space.
              </div>
            </div>
          )}

          {/* Roof Segments Management */}
          {roofPolygons.length > 0 && (
            <div style={{ marginBottom: "14px", padding: "10px", background: "#F0F7F2", borderRadius: "9px", border: "1px solid rgba(46,100,68,0.15)" }}>
              <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#2F7D52", marginBottom: "8px" }}>Roof Segments ({roofPolygons.length})</div>
              {roofPolygons.map((pts, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "5px", padding: "5px 6px", background: selectedRoofIdx === i ? "#E6F4EC" : "#FFF", borderRadius: "6px", border: selectedRoofIdx === i ? "1.5px solid #2F7D52" : "1px solid rgba(46,100,68,0.1)" }}>
                  <button onClick={() => { setSelectedRoofIdx(i); setSpec(roofSpecs[i] || DEFAULT_SPEC); }}
                    style={{ flex: 1, background: "none", border: "none", cursor: "pointer", textAlign: "left", fontSize: "11px", fontWeight: selectedRoofIdx === i ? "700" : "500", color: "#1B2B22", fontFamily: "Manrope, sans-serif" }}>
                    Roof {i + 1} {selectedRoofIdx === i ? "✓" : ""}
                  </button>
                  <span data-testid={`roof-stat-${i}`} style={{ fontSize: "9.5px", fontWeight: 700, color: "#2F7D52", background: "#E6F4EC", borderRadius: "100px", padding: "2px 7px", whiteSpace: "nowrap" }}>
                    {perRoofStats[i]?.count || 0}p · {((perRoofStats[i]?.watt || 0) / 1000).toFixed(1)}kWp
                  </span>
                  <button onClick={() => {
                    const offset = 0.0001;
                    const copied = pts.map(p => ({ lat: p.lat + offset, lng: p.lng + offset }));
                    setRoofPolygons(prev => [...prev, copied]);
                    setRoofSpecs(prev => [...prev, { ...(roofSpecs[i] || DEFAULT_SPEC) }]);
                    markDirty();
                    toast.success(`Roof ${i + 1} copied`);
                  }} style={{ background: "none", border: "none", cursor: "pointer", color: "#2F7D52", fontSize: "11px", padding: "2px" }} title="Copy roof">
                    Copy
                  </button>
                  <button onClick={() => {
                    if (roofPolygons.length <= 1) { toast.error("Cannot delete the only roof"); return; }
                    setRoofPolygons(prev => prev.filter((_, idx) => idx !== i));
                    setRoofSpecs(prev => prev.filter((_, idx) => idx !== i));
                    if (selectedRoofIdx >= roofPolygons.length - 1) setSelectedRoofIdx(Math.max(0, roofPolygons.length - 2));
                    markDirty();
                  }} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: "11px", padding: "2px" }} title="Delete roof">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <div style={{ fontSize: "9px", color: "#5A7264", marginTop: "4px" }}>
                Select a roof to edit its specs. Shift+drag on map to move.
              </div>
            </div>
          )}

          {/* Calculate */}
          <button
            onClick={calculateWithFeedback}
            disabled={calculating || !roofPolygons.length}
            data-testid="calculate-btn"
            style={{ width: "100%", padding: "11px 0", borderRadius: "10px", fontWeight: "700", fontSize: "13px", fontFamily: "Manrope, sans-serif", border: "none", cursor: calculating || !roofPolygons.length ? "not-allowed" : "pointer", opacity: !roofPolygons.length ? 0.45 : 1, background: "#2F7D52", color: "white", boxShadow: "0 3px 12px rgba(47,125,82,0.25)", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "10px" }}
          >
            <Cpu size={14} />{calculating ? "Calculating…" : "Calculate Layout"}
          </button>

          {/* Results */}
          {layout && (
            <div style={{ background: "linear-gradient(135deg, #F0FAF4, #F5F8F5)", border: "1px solid rgba(47,125,82,0.2)", borderRadius: "12px", padding: "14px", borderTop: "2px solid #FFB703" }}>
              <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8AA496", marginBottom: "10px" }}>Layout Results</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {[
                  ["Panels",      layout.totalPanels,                                            "#2F7D52"],
                  ["Capacity",    `${layout.totalKwp} kWp`,                                      "#D97706"],
                  ["Annual Est.", `${annualKwh.toLocaleString("en-IN")} kWh`,                    "#2F7D52"],
                  ["Inverter AC", `${inverterAcKw} kW`,                                          "#D97706"],
                  ["Roof Area",   `${layout.totalAreaM2} m²`,                                    "#5A7264"],
                  ["Density",     layout.totalAreaM2 > 0 ? `${((layout.totalKwp * 1000) / layout.totalAreaM2).toFixed(0)} W/m²` : "—", "#5A7264"],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: "#FFFFFF", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "8px", padding: "8px 10px" }}>
                    <div style={{ fontSize: "9px", color: "#8AA496", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "2px" }}>{label}</div>
                    <div data-testid={`result-${String(label).toLowerCase().replace(/\s+/g, "-")}`} style={{ fontSize: "16px", fontWeight: "800", color, fontFamily: "Outfit, sans-serif" }}>{val}</div>
                  </div>
                ))}
              </div>
              {layout.capped && (
                <div data-testid="capped-warning" style={{ marginTop: "10px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "8px", padding: "8px 10px", fontSize: "10.5px", fontWeight: 600, color: "#B91C1C", fontFamily: "Manrope, sans-serif" }}>
                  ⚠ Preview capped at 3,000 panels — totals are approximate. Zoom in and split the roof into segments for an accurate design.
                </div>
              )}
              <button onClick={() => setShow3D(true)} data-testid="results-view-3d-btn"
                style={{ marginTop: "10px", width: "100%", padding: "8px 0", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "11.5px", fontWeight: 700, fontFamily: "Manrope, sans-serif", background: "#0A1128", color: "#FFB703", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <Box size={13} /> View in 3D
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 14px", borderTop: "1px solid rgba(46,100,68,0.08)", flexShrink: 0, display: "flex", flexDirection: "column", gap: "7px" }}>
          <div style={{ display: "flex", gap: "7px" }}>
            <button onClick={() => saveDesign(false)} disabled={saving} data-testid="save-design-btn"
              style={{ flex: 1, padding: "9px 0", borderRadius: "9px", fontWeight: "600", fontSize: "12px", fontFamily: "Manrope, sans-serif", border: "1px solid rgba(47,125,82,0.25)", cursor: saving ? "not-allowed" : "pointer", background: "#F0F7F2", color: "#2F7D52", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#E6F4EC"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#F0F7F2"; }}
            >
              <Save size={13} />{saving ? "Saving…" : currentDesignId ? "Save" : "Save New"}
            </button>
            <button onClick={exportPDF} disabled={exporting || !layout} data-testid="export-pdf-btn"
              style={{ flex: 1, padding: "9px 0", borderRadius: "9px", fontWeight: "600", fontSize: "12px", fontFamily: "Manrope, sans-serif", border: "none", cursor: exporting || !layout ? "not-allowed" : "pointer", opacity: !layout ? 0.5 : 1, background: "#2F7D52", color: "white", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", boxShadow: "0 2px 8px rgba(47,125,82,0.25)" }}
              onMouseEnter={e => { if (!exporting && layout) e.currentTarget.style.background = "#266B44"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#2F7D52"; }}
            >
              <FileDown size={13} />{exporting ? "…" : "Export PDF"}
            </button>
          </div>

          <button onClick={() => { setShowSaved(!showSaved); if (!showSaved) fetchSavedDesigns(); }}
            data-testid="saved-designs-toggle"
            style={{ width: "100%", padding: "8px 0", borderRadius: "9px", fontWeight: "600", fontSize: "11px", fontFamily: "Manrope, sans-serif", border: "1px solid rgba(46,100,68,0.12)", cursor: "pointer", background: "#F5F8F5", color: "#5A7264", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
          >
            <Layers size={12} />Saved Designs ({savedDesigns.length})
            <ChevronDown size={11} style={{ transform: showSaved ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </button>

          {showSaved && (
            <div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid rgba(46,100,68,0.1)", borderRadius: "9px", background: "#FFFFFF" }}>
              {savedDesigns.length === 0
                ? <div style={{ fontSize: "11px", color: "#8AA496", textAlign: "center", padding: "12px" }}>No saved designs yet</div>
                : savedDesigns.map(d => (
                    <div key={d.id} style={{ display: "flex", alignItems: "center", borderBottom: "1px solid rgba(46,100,68,0.06)" }}>
                      <button onClick={() => loadDesign(d)}
                        data-testid={`load-design-${d.id}`}
                        style={{ flex: 1, textAlign: "left", background: "none", border: "none", padding: "9px 12px", fontSize: "12px", color: "#1B2B22", cursor: "pointer", fontFamily: "Manrope, sans-serif" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#F0F7F2"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
                      >
                        <div style={{ fontWeight: "600" }}>{d.name}</div>
                        {d.client_name && <div style={{ fontSize: "10px", color: "#8AA496" }}>{d.client_name}</div>}
                        {d.layout && <div style={{ fontSize: "10px", color: "#2F7D52" }}>{d.layout.totalPanels} panels · {d.layout.totalKwp} kWp</div>}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteDesign(d.id); }}
                        style={{ background: "none", border: "none", padding: "6px 10px", cursor: "pointer", color: "#EF4444", fontSize: "12px" }}
                        title="Delete design"
                        data-testid={`delete-design-${d.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))
              }
            </div>
          )}
        </div>
      </div>

      {/* Spin keyframes */}
      <style>{`@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Typed-dimensions dialog (roofs & obstacles) ─────────────────────────────
function DimensionsDialog({ req, roofPolygons, obstaclePolygons, mapRef, onClose, onApply }) {
  const isNew = req.type.startsWith("new");
  const pts = isNew ? null : (req.type === "roof" ? roofPolygons : obstaclePolygons)[req.idx];
  const rectLike = !pts || isRectangle(pts);
  const stats = pts ? edgeStats(pts) : [];
  const [mode, setMode] = useState(rectLike ? "rect" : "edges");
  const [rect, setRect] = useState(() => {
    if (pts && pts.length === 4) {
      const e = edgeStats(pts);
      return { width: +e[0].length.toFixed(2), length: +e[1].length.toFixed(2), bearing: Math.round(e[0].bearing) };
    }
    return { width: 20, length: 12, bearing: 90 };
  });
  const [edges, setEdges] = useState(() => stats.map(e => +e.length.toFixed(2)));
  const label = req.type === "roof" || req.type === "new-roof" ? "Roof" : "Obstacle";
  const inp = { width: "100%", padding: "7px 9px", borderRadius: "7px", border: "1px solid #D1D5DB", fontSize: "12px", fontFamily: "Manrope, sans-serif" };
  const lbl = { fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "#8AA496", display: "block", marginBottom: "3px" };

  const applyRect = () => {
    const w = parseFloat(rect.width), l = parseFloat(rect.length), b = parseFloat(rect.bearing) || 0;
    if (!(w > 0) || !(l > 0)) { toast.error("Width and length must be positive"); return; }
    if (w > 500 || l > 500) { toast.error("That looks too large (max 500 m per side)"); return; }
    let center;
    if (pts) center = { lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length, lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length };
    else { const c = mapRef.current?.getCenter(); center = c ? { lat: c.lat, lng: c.lng } : null; }
    if (!center) { toast.error("Map not ready"); return; }
    onApply(rectangleCentredOn(center, w, l, b));
  };
  const applyEdges = () => {
    let out = pts;
    for (let i = 0; i < edges.length; i++) {
      const v = parseFloat(edges[i]);
      if (!(v > 0)) { toast.error(`Edge ${i + 1} must be positive`); return; }
      if (Math.abs(v - stats[i].length) > 0.005) out = resizeEdge(out, i, v);
    }
    onApply(out);
  };

  return (
    <>
      <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(27,43,34,0.25)" }} />
      <div data-testid="dimensions-dialog" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 3001, background: "#fff", borderRadius: "12px", boxShadow: "0 16px 48px rgba(27,43,34,0.25)", padding: "18px", width: "360px", fontFamily: "Manrope, sans-serif" }}>
        <div style={{ fontSize: "14px", fontWeight: 800, color: "#1B2B22", marginBottom: "4px" }}>
          {isNew ? `New ${label.toLowerCase()} by size` : `${label} ${req.idx + 1} — set dimensions`}
        </div>
        <div style={{ fontSize: "11px", color: "#64748B", marginBottom: "12px" }}>
          {isNew ? "Placed at the current map centre; drag it into position afterwards." : "The shape keeps its position (centre) and orientation."}
        </div>
        {!isNew && (
          <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
            {[["rect", "Rectangle"], ["edges", `Edge lengths (${stats.length})`]].map(([m, t]) => (
              <button key={m} onClick={() => setMode(m)} style={{ flex: 1, padding: "6px", borderRadius: "7px", border: mode === m ? "1.5px solid #2F7D52" : "1px solid #E5E7EB", background: mode === m ? "#F0F7F2" : "#fff", fontSize: "11px", fontWeight: 700, color: mode === m ? "#2F7D52" : "#64748B", cursor: "pointer" }}>{t}</button>
            ))}
          </div>
        )}
        {mode === "rect" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            <div><label style={lbl}>Width (m)</label><input type="number" step="0.01" min="0.1" value={rect.width} onChange={e => setRect(r => ({ ...r, width: e.target.value }))} style={inp} data-testid="dims-width" autoFocus /></div>
            <div><label style={lbl}>Length (m)</label><input type="number" step="0.01" min="0.1" value={rect.length} onChange={e => setRect(r => ({ ...r, length: e.target.value }))} style={inp} data-testid="dims-length" /></div>
            <div><label style={lbl}>Bearing (°)</label><input type="number" step="1" min="0" max="359" value={rect.bearing} onChange={e => setRect(r => ({ ...r, bearing: e.target.value }))} style={inp} title="Compass direction of the width edge: 90 = east-west" /></div>
            {!rectLike && !isNew && <div style={{ gridColumn: "1 / -1", fontSize: "10.5px", color: "#B45309" }}>This shape is not a rectangle — applying will replace it with one of these dimensions.</div>}
          </div>
        ) : (
          <div style={{ maxHeight: "220px", overflowY: "auto" }}>
            {stats.map((e, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <span style={{ fontSize: "11px", color: "#64748B", width: "84px" }}>Edge {i + 1} <span style={{ color: "#94A3B8" }}>({Math.round(e.bearing)}°)</span></span>
                <input type="number" step="0.01" min="0.1" value={edges[i]} onChange={ev => setEdges(arr => arr.map((x, j) => j === i ? ev.target.value : x))} style={{ ...inp, width: "110px" }} data-testid={`dims-edge-${i}`} />
                <span style={{ fontSize: "11px", color: "#94A3B8" }}>m</span>
              </div>
            ))}
            <div style={{ fontSize: "10.5px", color: "#64748B", marginTop: "4px" }}>Changing an edge slides its end vertex along the edge; the next vertex moves with it so rectangles stay rectangular.</div>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
          <button onClick={onClose} style={{ padding: "7px 12px", borderRadius: "7px", border: "1px solid #D1D5DB", background: "#fff", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
          <button onClick={mode === "rect" ? applyRect : applyEdges} data-testid="dims-apply" style={{ padding: "7px 14px", borderRadius: "7px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>Apply</button>
        </div>
      </div>
    </>
  );
}
